import Phaser from 'phaser';
import { helpUI } from './help/helpUI.js';
import { sessionStorage } from './storage/sessionStorage.js';
import { logger } from './utils/logger.js';
import { PHASER_CONFIG } from './config/AppConfig.js';

// Simple scene for sprite loading and editing
class MinigameScene extends Phaser.Scene {
    constructor() {
        super({ key: 'MinigameScene' });
    }

    preload() {
        // Scene is ready for dynamic sprite loading
    }

    create() {
        // Set up basic input system
        this.cursors = this.input.keyboard.createCursorKeys();
        
        // Set this scene as the current scene
        window.currentGameScene = this;
        
        // Enable physics for sprite manipulation
        this.physics.world.enable([]);
    }

    updateStatus(_message) {
        // Status updates are handled by the UI layer
    }

    // Method to load a sprite atlas dynamically
    loadSpriteAtlas(key, imagePath, jsonPath) {
        this.load.atlas(key, imagePath, jsonPath);
        this.load.start();
    }

    // Reset/clear any rendered sprites
    resetSprites() {
        this.children.removeAll();
    }
}

// Game configuration - Fixed at 30 FPS
const config = {
    type: Phaser.AUTO,
    width: window.innerWidth,
    height: window.innerHeight,
    parent: 'gameContainer',
    render: {
        antialias: false,
        pixelArt: true,
        roundPixels: true
    },
    fps: PHASER_CONFIG.fps,
    physics: {
        default: 'arcade',
        arcade: {
            gravity: { y: 0 },
            debug: false
        }
    },
    scene: MinigameScene,
    backgroundColor: '#2c3e50'
};

// Initialize the game
const game = new Phaser.Game(config);

// Handle window resize
window.addEventListener('resize', () => {
    if (game && game.scale) {
        game.scale.resize(window.innerWidth, window.innerHeight);
    }
});

// Add keyboard shortcut to refresh input (press R key)
window.addEventListener('keydown', (e) => {
    if (e.key === 'r' || e.key === 'R') {
        if (game && game.scale) {
            game.scale.refresh();
        }
    }
    // Global help shortcut - works anywhere in the app
    if (e.key === '?' || e.key === 'h' || e.key === 'H') {
        // Only show help if we're not typing in an input field
        if (!['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
            e.preventDefault();
            helpUI.show();
        }
    }
});

// DEBUG: Check canvas setup
setTimeout(() => {
    // Canvas setup verification - no action needed
}, 1000);

// Developer Console System
class DevConsole {
    constructor() {
        this.startTime = Date.now();
        this.eventCount = 0;
        this.targetFps = 10;
        this.setupUI();
        this.setupStats();
    }
    
    setupUI() {
        // Console toggle with three states: visible -> minimized -> hidden -> visible
        const toggleConsole = document.getElementById('toggleConsole');
        if (toggleConsole) {
            toggleConsole.addEventListener('click', () => {
                const console = document.getElementById('devConsole');
                if (console.classList.contains('hidden')) {
                    // hidden -> visible
                    console.classList.remove('hidden');
                    console.classList.remove('minimized');
                } else if (console.classList.contains('minimized')) {
                    // minimized -> hidden
                    console.classList.add('hidden');
                    console.classList.remove('minimized');
                } else {
                    // visible -> minimized
                    console.classList.add('minimized');
                }
            });
        }
        
        // Console minimize button
        const consoleMinimizeBtn = document.getElementById('consoleMinimizeBtn');
        if (consoleMinimizeBtn) {
            consoleMinimizeBtn.addEventListener('click', () => {
                const console = document.getElementById('devConsole');
                const isMinimized = console.classList.contains('minimized');
                
                if (isMinimized) {
                    // Restore to full view
                    console.classList.remove('minimized');
                    consoleMinimizeBtn.textContent = '−';
                } else {
                    // Minimize
                    console.classList.add('minimized');
                    consoleMinimizeBtn.textContent = '+';
                }
            });
        }
        
        // FPS is now fixed at 30 - no selector needed
        
        // Upload toggle button
        const uploadToggle = document.getElementById('uploadToggle');
        if (uploadToggle) {
            uploadToggle.addEventListener('click', () => {
                const uploadPanel = document.getElementById('uploadPanel');
                if (uploadPanel) {
                    uploadPanel.style.display = uploadPanel.style.display === 'none' ? 'block' : 'none';
                }
            });
        }
    }
    
    setupStats() {
        // Update stats every second
        setInterval(() => {
            this.updateStats();
        }, 1000);
    }
    
    // Removed setFpsCap method - FPS is now set only at game initialization
    
    updateStats() {
        // FPS Counter
        if (game && game.loop) {
            document.getElementById('fpsCounter').textContent = Math.round(game.loop.actualFps);
        }
        
        // Memory usage (if available)
        if (performance.memory) {
            const memUsed = Math.round(performance.memory.usedJSHeapSize / 1024 / 1024);
            document.getElementById('memoryUsage').textContent = memUsed + 'MB';
        }
        // Event count
        document.getElementById('eventCount').textContent = this.eventCount;
    }
    
    logEvent(_message) {
        this.eventCount++;
    }
    
    updateEngineStatus(status) {
        const element = document.getElementById('engineStatus');
        if (element) {
            element.textContent = `Phaser Engine: ${status}`;
        }
    }
}

// Sprite Tools System (separate from DevConsole)
class SpriteTools {
    constructor() {
        this.debugSprites = [];
        this.frameUpdates = {};
        this.currentSelection = null;
        this.isInitializing = true; // Flag to prevent auto-opening during init
        
        // IMMEDIATELY force old modal to be completely hidden via CSS
        const style = document.createElement('style');
        style.textContent = `
            #spriteEditorModal {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
                pointer-events: none !important;
            }
        `;
        document.head.appendChild(style);
        
        // Check for saved data before setting up UI
        this.setupSessionStorage();
        
        this.setupUI();
        this.setupUploadInterface();
        
        // Allow normal operation after a short delay
        setTimeout(() => {
            this.isInitializing = false;
        }, 1000);
    }
    
    setupUI() {
// Debug: Check initial state of modals
        const spritePanel = document.getElementById('spriteToolsPanel');
        const spriteModal = document.getElementById('spriteEditorModal');
// FORCE modals to be hidden - this is critical
        if (spritePanel) {
            spritePanel.classList.add('hidden');
            spritePanel.style.display = 'none'; // Double protection
        }

        // Show upload panel initially
        const uploadPanel = document.getElementById('uploadPanel');
        if (uploadPanel) {
            uploadPanel.style.display = 'block';
        }
        if (spriteModal) {
            spriteModal.classList.add('hidden');
            spriteModal.style.display = 'none'; // Double protection
        }
        
        // Open sprite tools from hamburger menu
        const openBtn = document.getElementById('openSpriteTools');
        if (openBtn) {
            openBtn.addEventListener('click', () => {
                const panel = document.getElementById('spriteToolsPanel');
                panel.classList.remove('hidden');
                panel.style.display = 'block'; // Force display

                document.getElementById('gameDropdown').classList.add('hidden');
                document.getElementById('hamburgerMenu').classList.remove('active');
            });
        }
        
        // Close sprite tools
        const closeBtn = document.getElementById('closeSpriteTools');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => {
                const panel = document.getElementById('spriteToolsPanel');
                panel.classList.add('hidden');
                panel.style.display = 'none'; // Force hidden
            });
        }
        
        this.setupSpriteRenderer();
    }
    
    setupSpriteRenderer() {
        const spriteAtlasSelect = document.getElementById('spriteAtlas');
        const spriteFrameSelect = document.getElementById('spriteFrame');
        const renderBtn = document.getElementById('renderSprite');
        const clearBtn = document.getElementById('clearSprites');
        const spriteInfo = document.getElementById('spriteInfo');
        this.debugSprites = []; // Track debug sprites for cleanup
        
        // Check if atlas has a default value
        if (spriteAtlasSelect) {
            // Atlas select element is available
        }
        
        // Atlas selection handler
        spriteAtlasSelect.addEventListener('change', () => {
            const atlasKey = spriteAtlasSelect.value;
            this.populateFrameOptions(atlasKey, spriteFrameSelect);
        });
        
        // Render sprite handler
        renderBtn.addEventListener('click', () => {
            const atlasKey = spriteAtlasSelect.value;
            const frameKey = spriteFrameSelect.value;
            if (atlasKey && frameKey) {
                this.renderSpriteOnGame(atlasKey, frameKey, spriteInfo);
            }
        });
        
        // Clear sprites handler
        clearBtn.addEventListener('click', () => {
            this.clearDebugSprites();
            this.resetUploadInterface();
            spriteInfo.textContent = 'Upload a spritesheet to begin';
        });
        
        // Edit spritesheet handler
        const editBtn = document.getElementById('editSpritesheet');
        if (editBtn) {
            editBtn.addEventListener('click', () => {
                const atlasKey = spriteAtlasSelect.value;
                if (atlasKey) {
                    this.openSpriteEditor(atlasKey);
                } else {
                    spriteInfo.textContent = 'Please select an atlas first';
                    spriteInfo.style.color = '#ff6b6b';
                    setTimeout(() => {
                        spriteInfo.style.color = '';
                    }, 2000);
                }
            });
        } else {
            logger.error('[SPRITE TOOLS] Edit button not found!');
        }
    }

    setupUploadInterface() {
        // Setup main upload panel
        this.setupMainUploadPanel();

        // Setup small upload area in sprite tools
        this.setupSmallUploadArea();
    }
    
    setupMainUploadPanel() {
        // Initialize upload state
        this.uploadState = {
            spritesheetFile: null,
            jsonFile: null,
            spritesheetData: null,
            jsonData: null,
            currentStep: 1
        };
        
        // Setup spritesheet upload (step 1)
        this.setupSpritesheetUpload();
        
        // Setup step navigation
        this.setupStepNavigation();
        
        // Setup JSON upload (step 3)
        this.setupJsonUpload();
    }
    
    setupSmallUploadArea() {
        const uploadAreaSmall = document.getElementById('uploadAreaSmall');
        const fileInput = document.getElementById('spritesheetFileSmall');
        
        if (!uploadAreaSmall || !fileInput) {
            logger.warn('[SPRITE TOOLS] Small upload area elements not found');
            return;
        }
        
        // Click to select file
        uploadAreaSmall.addEventListener('click', () => {
            fileInput.click();
        });
        
        // File input change
        fileInput.addEventListener('change', (e) => {
            const file = e.target.files[0];
            if (file) {
                this.handleFileUpload(file, 'small');
            }
        });
        
        // Drag and drop functionality
        uploadAreaSmall.addEventListener('dragover', (e) => {
            e.preventDefault();
            uploadAreaSmall.classList.add('dragover');
        });
        
        uploadAreaSmall.addEventListener('dragleave', () => {
            uploadAreaSmall.classList.remove('dragover');
        });
        
        uploadAreaSmall.addEventListener('drop', (e) => {
            e.preventDefault();
            uploadAreaSmall.classList.remove('dragover');
            
            const files = e.dataTransfer.files;
            if (files.length > 0) {
                const file = files[0];
                if (file.type.startsWith('image/')) {
                    this.handleFileUpload(file, 'small');
                } else {
                    this.showUploadError('Please upload a valid image file', 'small');
                }
            }
        });
    }
    
    setupStepNavigation() {
        // Atlas choice buttons in step 1
        const createAtlasBtn = document.getElementById('createAtlasBtn');
        const uploadAtlasBtn = document.getElementById('uploadAtlasBtn');
        
        // Navigation buttons in step 2
        const backToChoiceBtn = document.getElementById('backToChoiceBtn');
        const processWithJsonBtn = document.getElementById('processWithJsonBtn');
        
        if (createAtlasBtn) {
            createAtlasBtn.addEventListener('click', () => {
                this.processUploadWithoutJson();
            });
        }
        
        if (uploadAtlasBtn) {
            uploadAtlasBtn.addEventListener('click', () => {
                this.goToStep(2);
            });
        }
        
        if (backToChoiceBtn) {
            backToChoiceBtn.addEventListener('click', () => {
                this.goToStep(1);
                // Show atlas options since we're going back to Step 1 with spritesheet already uploaded
                const atlasOptions = document.getElementById('atlasOptions');
                if (atlasOptions && this.uploadState.spritesheetFile) {
                    atlasOptions.classList.remove('hidden');
                }
            });
        }
        
        if (processWithJsonBtn) {
            processWithJsonBtn.addEventListener('click', () => {
                this.processUploadWithJson();
            });
        }
    }
    
    goToStep(step) {
        // Hide all steps
        for (let i = 1; i <= 2; i++) {
            const stepElement = document.getElementById(`uploadStep${i}`);
            if (stepElement) {
                stepElement.classList.add('hidden');
            }
        }
        
        // Show target step
        const targetStep = document.getElementById(`uploadStep${step}`);
        if (targetStep) {
            targetStep.classList.remove('hidden');
        }
        
        // Handle atlas options visibility for step 1
        const atlasOptions = document.getElementById('atlasOptions');
        if (step === 1 && atlasOptions) {
            // Show atlas options if spritesheet is uploaded
            if (this.uploadState.spritesheetFile) {
                atlasOptions.classList.remove('hidden');
            } else {
                atlasOptions.classList.add('hidden');
            }
        }
        
        this.uploadState.currentStep = step;
    }
    
    resetToStep1() {
        // Clear upload state except step tracking
        this.uploadState = {
            spritesheetFile: null,
            jsonFile: null,
            spritesheetData: null,
            jsonData: null,
            currentStep: 1
        };
        
        // Clear file inputs
        const spritesheetInput = document.getElementById('spritesheetFile');
        const jsonInput = document.getElementById('jsonFile');
        
        if (spritesheetInput) spritesheetInput.value = '';
        if (jsonInput) jsonInput.value = '';
        
        // Reset upload areas to initial state
        this.resetUploadAreas();
        
        // Re-setup event listeners
        this.setupSpritesheetUpload();
        this.setupJsonUpload();
        
        // Go back to step 1
        this.goToStep(1);
}
    
    resetUploadAreas() {
        // Reset spritesheet upload area
        const uploadArea = document.getElementById('uploadArea');
        if (uploadArea) {
            uploadArea.innerHTML = `
                <div class="upload-prompt">
                    <div class="upload-icon">🖼️</div>
                    <p>Click to select or drag & drop a spritesheet image</p>
                    <small>Supported formats: PNG, JPG, JPEG, GIF</small>
                </div>
            `;
        }
        
        // Reset JSON upload area
        const jsonUploadArea = document.getElementById('jsonUploadArea');
        if (jsonUploadArea) {
            jsonUploadArea.innerHTML = `
                <div class="upload-prompt">
                    <div class="upload-icon">📄</div>
                    <p>Click to select or drag & drop atlas JSON file</p>
                    <small>TexturePacker or similar format</small>
                </div>
            `;
        }
        
        // Hide upload info sections
        const uploadInfo = document.getElementById('uploadInfo');
        const jsonUploadInfo = document.getElementById('jsonUploadInfo');
        
        if (uploadInfo) {
            uploadInfo.innerHTML = '';
            uploadInfo.classList.add('hidden');
        }
        if (jsonUploadInfo) {
            jsonUploadInfo.innerHTML = '';
            jsonUploadInfo.classList.add('hidden');
        }
        
        // Hide atlas options
        const atlasOptions = document.getElementById('atlasOptions');
        if (atlasOptions) {
            atlasOptions.classList.add('hidden');
        }
        
        // Hide name input section
        const nameSection = document.getElementById('spritesheetNameSection');
        if (nameSection) {
            nameSection.classList.add('hidden');
        }
        
        // Clear name input value
        const nameInput = document.getElementById('spritesheetName');
        if (nameInput) {
            nameInput.value = '';
        }
    }
    
    setupSpritesheetUpload() {
        const uploadArea = document.getElementById('uploadArea');
        const fileInput = document.getElementById('spritesheetFile');
        
        if (!uploadArea || !fileInput) {
            logger.warn('[SPRITE TOOLS] Spritesheet upload elements not found');
            return;
        }
        
        // Click to select file
        uploadArea.addEventListener('click', () => {
            fileInput.click();
        });
        
        // File input change
        fileInput.addEventListener('change', (e) => {
            const file = e.target.files[0];
            if (file) {
                this.handleSpritesheetUpload(file);
            }
        });
        
        // Drag and drop functionality
        uploadArea.addEventListener('dragover', (e) => {
            e.preventDefault();
            uploadArea.classList.add('dragover');
        });
        
        uploadArea.addEventListener('dragleave', () => {
            uploadArea.classList.remove('dragover');
        });
        
        uploadArea.addEventListener('drop', (e) => {
            e.preventDefault();
            uploadArea.classList.remove('dragover');
            
            const files = e.dataTransfer.files;
            if (files.length > 0) {
                const file = files[0];
                if (file.type.startsWith('image/')) {
                    this.handleSpritesheetUpload(file);
                } else {
                    this.showUploadError('Please upload a valid image file', 'spritesheet');
                }
            }
        });
        
        // Setup name input validation
        this.setupNameInput();
    }
    
    setupNameInput() {
        const nameInput = document.getElementById('spritesheetName');
        if (!nameInput) return;
        
        nameInput.addEventListener('input', (e) => {
            const value = e.target.value;
            const sanitized = value.replace(/[^a-zA-Z0-9_]/g, '_');
            
            if (value !== sanitized) {
                // Update the input with sanitized value
                e.target.value = sanitized;
            }
            
            // Update visual feedback
            this.updateNameInputFeedback(sanitized);
        });
        
        nameInput.addEventListener('blur', (e) => {
            // Clean up multiple underscores and trim
            const cleaned = e.target.value
                .replace(/_{2,}/g, '_')
                .replace(/^_+|_+$/g, '');
            e.target.value = cleaned;
            this.updateNameInputFeedback(cleaned);
        });
    }
    
    updateNameInputFeedback(value) {
        const nameInput = document.getElementById('spritesheetName');
        const hint = nameInput?.parentElement?.querySelector('.name-hint');
        
        if (!nameInput || !hint) return;
        
        if (value.length === 0) {
            hint.textContent = 'Leave empty to use filename. Alphanumeric characters and underscores only.';
            hint.style.color = '#999';
            nameInput.style.borderColor = 'rgba(255, 255, 255, 0.2)';
        } else if (value.length > 20) {
            hint.textContent = 'Name too long. Maximum 20 characters.';
            hint.style.color = '#f44336';
            nameInput.style.borderColor = '#f44336';
        } else if (!/^[a-zA-Z0-9_]+$/.test(value)) {
            hint.textContent = 'Invalid characters removed. Use only letters, numbers, and underscores.';
            hint.style.color = '#ff9800';
            nameInput.style.borderColor = '#ff9800';
        } else {
            hint.textContent = `Atlas key will be: ${this.generateAtlasKey(value, 'spritesheet')}`;
            hint.style.color = '#4CAF50';
            nameInput.style.borderColor = '#4CAF50';
        }
    }
    
    setupJsonUpload() {
        const jsonUploadArea = document.getElementById('jsonUploadArea');
        const jsonFileInput = document.getElementById('jsonFile');
        
        if (!jsonUploadArea || !jsonFileInput) {
            logger.warn('[SPRITE TOOLS] JSON upload elements not found');
            return;
        }
        
        // Click to select file
        jsonUploadArea.addEventListener('click', () => {
            jsonFileInput.click();
        });
        
        // File input change
        jsonFileInput.addEventListener('change', (e) => {
            const file = e.target.files[0];
            if (file) {
                this.handleJsonUpload(file);
            }
        });
        
        // Drag and drop functionality
        jsonUploadArea.addEventListener('dragover', (e) => {
            e.preventDefault();
            jsonUploadArea.classList.add('dragover');
        });
        
        jsonUploadArea.addEventListener('dragleave', () => {
            jsonUploadArea.classList.remove('dragover');
        });
        
        jsonUploadArea.addEventListener('drop', (e) => {
            e.preventDefault();
            jsonUploadArea.classList.remove('dragover');
            
            const files = e.dataTransfer.files;
            if (files.length > 0) {
                const file = files[0];
                if (file.type === 'application/json' || file.name.endsWith('.json')) {
                    this.handleJsonUpload(file);
                } else {
                    this.showUploadError('Please upload a valid JSON file', 'json');
                }
            }
        });
    }
    
    handleSpritesheetUpload(file) {
// Validate file type
        if (!file.type.startsWith('image/')) {
            this.showUploadError('Please upload a valid image file', 'spritesheet');
            return;
        }
        
        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            this.showUploadError('File size must be less than 10MB', 'spritesheet');
            return;
        }
        
        this.uploadState.spritesheetFile = file;
        
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                this.uploadState.spritesheetData = {
                    image: img,
                    file: file,
                    dataUrl: e.target.result
                };
                
                this.showUploadSuccess(file, null, 'spritesheet');
                this.updateUploadState();
                
                // Show atlas options after successful upload
                setTimeout(() => {
                    const atlasOptions = document.getElementById('atlasOptions');
                    if (atlasOptions) {
                        atlasOptions.classList.remove('hidden');
                    }
                }, 1000);
};
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    }
    
    handleJsonUpload(file) {
// Validate file type
        if (!file.type.includes('json') && !file.name.endsWith('.json')) {
            this.showUploadError('Please upload a valid JSON file', 'json');
            return;
        }
        
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            this.showUploadError('JSON file size must be less than 5MB', 'json');
            return;
        }
        
        this.uploadState.jsonFile = file;
        
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const jsonData = JSON.parse(e.target.result);
                this.uploadState.jsonData = jsonData;
                
                this.showUploadSuccess(file, null, 'json');
                this.updateUploadState();
} catch (error) {
                logger.error('[SPRITE TOOLS] JSON parsing error:', error);
                this.showUploadError('Invalid JSON file format', 'json');
            }
        };
        reader.readAsText(file);
    }
    
    updateUploadState() {
        const processWithJsonBtn = document.getElementById('processWithJsonBtn');
        if (processWithJsonBtn) {
            // Enable process button if we have JSON data
            processWithJsonBtn.disabled = !this.uploadState.jsonData;
        }
    }
    
    processUploadWithoutJson() {
        if (!this.uploadState.spritesheetData) {
            this.showUploadError('Please upload a spritesheet first', 'spritesheet');
            return;
        }
// Generate atlas key using custom name
        const customName = this.getCustomSpritesheetName();
        const fallbackName = this.uploadState.spritesheetData.file.name.split('.').slice(0, -1).join('.');
        const atlasKey = this.generateAtlasKey(customName, fallbackName);
        
        // Load image into Phaser
        this.loadImageIntoPhaser(
            this.uploadState.spritesheetData.image, 
            atlasKey, 
            this.uploadState.spritesheetData.file.name
        );
        
        // Hide upload panel and show sprite tools
        const uploadPanel = document.getElementById('uploadPanel');
        if (uploadPanel) {
            uploadPanel.style.display = 'none';
        }
        
        // Save to localStorage before clearing upload state
        sessionStorage.saveToLocalStorage(this.uploadState.spritesheetData);
        
        // Open sprite editor
        this.createFullScreenSpriteEditor(atlasKey);
        
        // Clear upload state
        this.clearUpload();
}
    
    processUploadWithJson() {
        if (!this.uploadState.spritesheetData) {
            this.showUploadError('Please upload a spritesheet first', 'spritesheet');
            return;
        }
        
        if (!this.uploadState.jsonData) {
            this.showUploadError('Please upload a JSON file first', 'json');
            return;
        }
// Generate atlas key using custom name
        const customName = this.getCustomSpritesheetName();
        const fallbackName = this.uploadState.spritesheetData.file.name.split('.').slice(0, -1).join('.');
        const atlasKey = this.generateAtlasKey(customName, fallbackName);
        
        // Load image into Phaser
        this.loadImageIntoPhaser(
            this.uploadState.spritesheetData.image, 
            atlasKey, 
            this.uploadState.spritesheetData.file.name
        );
        
        // Apply JSON data to the texture
        this.applyJsonToTexture(atlasKey, this.uploadState.jsonData);
        
        // Hide upload panel and show sprite tools
        const uploadPanel = document.getElementById('uploadPanel');
        if (uploadPanel) {
            uploadPanel.style.display = 'none';
        }
        
        // Save to localStorage before clearing upload state (include JSON data)
        sessionStorage.saveToLocalStorage(this.uploadState.spritesheetData, this.uploadState.jsonData);
        
        // Open sprite editor
        this.createFullScreenSpriteEditor(atlasKey);
        
        // Clear upload state
        this.clearUpload();
}
    
    clearUpload() {
        this.uploadState = {
            spritesheetFile: null,
            jsonFile: null,
            spritesheetData: null,
            jsonData: null,
            currentStep: 1
        };
        
        // Clear file inputs
        const spritesheetInput = document.getElementById('spritesheetFile');
        const jsonInput = document.getElementById('jsonFile');
        
        if (spritesheetInput) spritesheetInput.value = '';
        if (jsonInput) jsonInput.value = '';
        
        // Clear upload info displays
        const uploadInfo = document.getElementById('uploadInfo');
        const jsonUploadInfo = document.getElementById('jsonUploadInfo');
        
        if (uploadInfo) uploadInfo.innerHTML = '';
        if (jsonUploadInfo) jsonUploadInfo.innerHTML = '';
        
        // Hide name input section and clear value
        const nameSection = document.getElementById('spritesheetNameSection');
        if (nameSection) {
            nameSection.classList.add('hidden');
        }
        
        const nameInput = document.getElementById('spritesheetName');
        if (nameInput) {
            nameInput.value = '';
        }
        
        // Update button state
        this.updateUploadState();
        
        // Reset to step 1
        this.goToStep(1);
}
    
    // === NAMING FUNCTIONALITY ===
    
    generateAtlasKey(customName = null, fallbackName = 'spritesheet') {
        const random = Math.random().toString(36).substring(2, 6);

        let baseName;
        
        if (customName && customName.trim()) {
            // Use custom name, sanitize it
            baseName = customName.trim()
                .replace(/[^a-zA-Z0-9_]/g, '_')
                .replace(/_{2,}/g, '_')
                .replace(/^_+|_+$/g, '')
                .toLowerCase();
        } else {
            // Use fallback name
            baseName = fallbackName.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase();
        }
        
        // Ensure name isn't empty and isn't too long
        if (!baseName) baseName = 'spritesheet';
        if (baseName.length > 20) baseName = baseName.substring(0, 20);
        
        return `${baseName}_${random}`;
    }
    
    getCustomSpritesheetName() {
        const nameInput = document.getElementById('spritesheetName');
        return nameInput ? nameInput.value.trim() : '';
    }
    
    // === SESSION STORAGE FUNCTIONALITY ===
    
    setupSessionStorage() {
        sessionStorage.checkForSavedData(
            (savedData) => this.restoreFromSavedData(savedData),
            () => logger.log('[SPRITE TOOLS] Starting fresh session')
        );
    }
    
    restoreFromSavedData(savedData) {
// Create image from saved data URL
        const img = new Image();
        img.onload = () => {
            // Recreate spritesheet data structure
            const spritesheetData = {
                file: {
                    name: savedData.spritesheet.name,
                    size: savedData.spritesheet.size
                },
                image: img
            };
            
            // Generate atlas key
            const timestamp = Date.now();
            const random = Math.random().toString(36).substring(2, 8);
            const atlasKey = `restored_${timestamp}_${random}`;
            
            // Load into Phaser
            this.loadImageIntoPhaser(img, atlasKey, spritesheetData.file.name);
            
            // Apply atlas data if available
            if (savedData.atlas) {
                setTimeout(() => {
                    this.applyJsonToTexture(atlasKey, savedData.atlas);
                    this.createFullScreenSpriteEditor(atlasKey);
                }, 100);
            } else {
                setTimeout(() => {
                    this.createFullScreenSpriteEditor(atlasKey);
                }, 100);
            }
            
            // Hide upload panel
            const uploadPanel = document.getElementById('uploadPanel');
            if (uploadPanel) {
                uploadPanel.style.display = 'none';
            }
};
        
        img.src = savedData.spritesheet.dataUrl;
    }
    
    applyJsonToTexture(atlasKey, jsonData) {
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(atlasKey);
        
        if (!texture) {
            logger.error('[SPRITE TOOLS] Texture not found:', atlasKey);
            return;
        }
        
        // Clear existing frames
        texture.frames = {};
        
        // Apply frames from JSON
        if (jsonData.frames) {
            if (Array.isArray(jsonData.frames)) {
                // TexturePacker format
                jsonData.frames.forEach(frameData => {
                    const frameName = frameData.filename.replace(/\.[^/.]+$/, ""); // Remove extension
                    texture.frames[frameName] = {
                        cutX: frameData.frame.x,
                        cutY: frameData.frame.y,
                        cutWidth: frameData.frame.w,
                        cutHeight: frameData.frame.h,
                        x: frameData.frame.x,
                        y: frameData.frame.y,
                        width: frameData.frame.w,
                        height: frameData.frame.h,
                        rotated: frameData.rotated || false,
                        trimmed: frameData.trimmed || false,
                        spriteSourceSize: frameData.spriteSourceSize || { x: 0, y: 0, w: frameData.frame.w, h: frameData.frame.h },
                        sourceSize: frameData.sourceSize || { w: frameData.frame.w, h: frameData.frame.h }
                    };
                });
            } else {
                // Object format
                Object.keys(jsonData.frames).forEach(frameName => {
                    const frameData = jsonData.frames[frameName];
                    texture.frames[frameName] = {
                        cutX: frameData.frame.x,
                        cutY: frameData.frame.y,
                        cutWidth: frameData.frame.w,
                        cutHeight: frameData.frame.h,
                        x: frameData.frame.x,
                        y: frameData.frame.y,
                        width: frameData.frame.w,
                        height: frameData.frame.h,
                        rotated: frameData.rotated || false,
                        trimmed: frameData.trimmed || false,
                        spriteSourceSize: frameData.spriteSourceSize || { x: 0, y: 0, w: frameData.frame.w, h: frameData.frame.h },
                        sourceSize: frameData.sourceSize || { w: frameData.frame.w, h: frameData.frame.h }
                    };
                });
            }
        }
}
    
    handleFileUpload(file, source = 'main') {
// Validate file type
        if (!file.type.startsWith('image/')) {
            this.showUploadError('Please upload a valid image file', source);
            return;
        }
        
        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            this.showUploadError('File size must be less than 10MB', source);
            return;
        }
        
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                // Generate unique atlas key
                const timestamp = Date.now();
                const random = Math.random().toString(36).substring(2, 8);
                const atlasKey = `uploaded_${timestamp}_${random}`;
                
                // Load into Phaser
                this.loadImageIntoPhaser(img, atlasKey, file.name);
                
                // Update UI
                this.showUploadSuccess(file, atlasKey, source);
};
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    }
    
    loadImageIntoPhaser(img, atlasKey, filename) {
        const scene = game.scene.getScene('MinigameScene');
        if (!scene) {
            logger.error('[SPRITE TOOLS] Scene not found');
            return;
        }
        
        try {
            // Remove existing texture if it exists
            if (scene.textures.exists(atlasKey)) {
                scene.textures.remove(atlasKey);
}
            
            // Create frame name from filename (remove extension)
            const frameName = filename.replace(/\.[^/.]+$/, '') || 'sprite';
            
            // Create a basic atlas with the entire image as one frame
            const atlasData = {
                frames: {
                    [frameName]: {
                        frame: { x: 0, y: 0, w: img.width, h: img.height },
                        rotated: false,
                        trimmed: false,
                        spriteSourceSize: { x: 0, y: 0, w: img.width, h: img.height },
                        sourceSize: { w: img.width, h: img.height }
                    }
                },
                meta: {
                    app: "Sprite Tool",
                    version: "1.0",
                    image: filename,
                    format: "RGBA8888",
                    size: { w: img.width, h: img.height },
                    scale: "1"
                }
            };
            
            // Create atlas from the image
            scene.textures.addAtlas(atlasKey, img, atlasData);
} catch (error) {
            logger.error('[SPRITE TOOLS] Error loading image into Phaser:', error);
        }
    }
    
    showUploadSuccess(file, atlasKey, source = 'main') {
        if (source === 'spritesheet') {
            // Update spritesheet upload area with minimal info
            const uploadArea = document.getElementById('uploadArea');
            
            if (uploadArea) {
                uploadArea.innerHTML = `
                    <div class="upload-icon">✅</div>
                    <p>Spritesheet uploaded successfully!</p>
                    <img src="${URL.createObjectURL(file)}" class="uploaded-image" alt="Uploaded spritesheet"><br>
                    <small>${file.name} (${(file.size / 1024).toFixed(1)} KB)</small>
                `;
            }
            
            // Show name input section for spritesheet uploads
            const nameSection = document.getElementById('spritesheetNameSection');
            if (nameSection) {
                nameSection.classList.remove('hidden');
                
                // Pre-fill with filename without extension
                const nameInput = document.getElementById('spritesheetName');
                if (nameInput) {
                    const baseName = file.name.split('.').slice(0, -1).join('.').replace(/[^a-zA-Z0-9_]/g, '_');
                    nameInput.value = baseName;
                }
            }
            
            // Keep upload-info hidden to reduce duplication
        } else if (source === 'json') {
            // Update JSON upload area with minimal info
            const jsonUploadArea = document.getElementById('jsonUploadArea');
            
            if (jsonUploadArea) {
                jsonUploadArea.innerHTML = `
                    <div class="upload-icon">✅</div>
                    <p>Atlas JSON uploaded successfully!</p>
                    <small>${file.name} (${(file.size / 1024).toFixed(1)} KB)</small>
                `;
            }
            
            // Keep JSON upload-info hidden to reduce duplication
        } else if (source === 'main') {
            // Update main upload panel (legacy)
            const uploadArea = document.getElementById('uploadArea');
            const uploadInfo = document.getElementById('uploadInfo');
            
            if (uploadArea) {
                uploadArea.innerHTML = `
                    <div class="upload-icon">✅</div>
                    <p>Spritesheet uploaded successfully!</p>
                    <img src="${URL.createObjectURL(file)}" class="uploaded-image" alt="Uploaded spritesheet">
                    <small>${file.name} (${(file.size / 1024).toFixed(1)} KB)</small>
                `;
            }
            
            if (uploadInfo) {
                uploadInfo.innerHTML = `
                    <div><strong>File:</strong> ${file.name}</div>
                    <div><strong>Size:</strong> ${(file.size / 1024).toFixed(1)} KB</div>
                    <div><strong>Type:</strong> ${file.type}</div>
                    <div class="upload-actions">
                        <button class="upload-btn" onclick="window.spriteTools.openSpriteToolsPanel()">Open Sprite Tools</button>
                    </div>
                `;
                uploadInfo.classList.remove('hidden');
            }
        } else {
            // Update small upload area
            const uploadAreaSmall = document.getElementById('uploadAreaSmall');
            const uploadInfoSmall = document.getElementById('uploadInfoSmall');
            
            if (uploadAreaSmall) {
                uploadAreaSmall.innerHTML = `
                    <div class="upload-prompt-small">
                        <span>✅ ${file.name}</span>
                    </div>
                `;
            }
            
            if (uploadInfoSmall) {
                uploadInfoSmall.innerHTML = `
                    <div>${file.name} (${(file.size / 1024).toFixed(1)} KB)</div>
                `;
                uploadInfoSmall.classList.remove('hidden');
            }
        }
        
        // Add to atlas dropdown
        const spriteAtlasSelect = document.getElementById('spriteAtlas');
        if (spriteAtlasSelect) {
            const option = document.createElement('option');
            option.value = atlasKey;
            option.textContent = file.name;
            spriteAtlasSelect.appendChild(option);
            spriteAtlasSelect.value = atlasKey;
            
            // Trigger change event to populate frames
            spriteAtlasSelect.dispatchEvent(new Event('change'));
        }
        
        const spriteInfo = document.getElementById('spriteInfo');
        if (spriteInfo) {
            spriteInfo.textContent = `Loaded: ${file.name}`;
        }
        
        // Auto-open sprite tools if uploaded from main panel
        if (source === 'main') {
            this.openSpriteToolsPanel();
        }
    }
    
    showUploadError(message, source = 'main') {
        if (source === 'spritesheet') {
            const uploadInfo = document.getElementById('uploadInfo');
            if (uploadInfo) {
                uploadInfo.innerHTML = `<div style="color: #f44336;">❌ ${message}</div>`;
                uploadInfo.classList.remove('hidden');
            }
        } else if (source === 'json') {
            const jsonUploadInfo = document.getElementById('jsonUploadInfo');
            if (jsonUploadInfo) {
                jsonUploadInfo.innerHTML = `<div style="color: #f44336;">❌ ${message}</div>`;
                jsonUploadInfo.classList.remove('hidden');
            }
        } else if (source === 'main') {
            const uploadInfo = document.getElementById('uploadInfo');
            if (uploadInfo) {
                uploadInfo.innerHTML = `<div style="color: #f44336;">❌ ${message}</div>`;
                uploadInfo.classList.remove('hidden');
            }
        } else {
            const uploadInfoSmall = document.getElementById('uploadInfoSmall');
            if (uploadInfoSmall) {
                uploadInfoSmall.innerHTML = `<div style="color: #f44336;">❌ ${message}</div>`;
                uploadInfoSmall.classList.remove('hidden');
            }
        }
    }
    
    openSpriteToolsPanel() {
        const panel = document.getElementById('spriteToolsPanel');
        if (panel) {
            panel.classList.remove('hidden');
            panel.style.display = 'block';
        }
        
        // Hide upload panel
        const uploadPanel = document.getElementById('uploadPanel');
        if (uploadPanel) {
            uploadPanel.style.display = 'none';
        }
}
    
    populateFrameOptions(atlasKey, frameSelect) {
        // Clear existing options
        frameSelect.innerHTML = '<option value="">Select Frame</option>';
        
        if (!atlasKey) return;
        
        const scene = game.scene.getScene('MinigameScene');
        if (!scene || !scene.textures) return;
        
        try {
            const texture = scene.textures.get(atlasKey);
            if (!texture) {
                logger.warn('[SPRITE RENDERER] Texture not found for atlas:', atlasKey);
                return;
            }
            
            if (!texture.frames) {
                logger.warn('[SPRITE RENDERER] No frames object found for atlas:', atlasKey);
                return;
            }
            
            // Get all frame names from the atlas
            const allFrameNames = Object.keys(texture.frames);
            const frameNames = allFrameNames.filter(name => name !== '__BASE');
            if (frameNames.length === 0) {
                logger.warn('[SPRITE RENDERER] No valid frames found after filtering for atlas:', atlasKey);
                return;
            }
            
            frameNames.sort();
            
            frameNames.forEach(frameName => {
                const option = document.createElement('option');
                option.value = frameName;
                option.textContent = frameName;
                frameSelect.appendChild(option);
            });
} catch (error) {
            logger.error('[SPRITE RENDERER] Error loading frames:', error);
        }
    }
    
    renderSpriteOnGame(atlasKey, frameKey, infoElement) {
        const scene = game.scene.getScene('MinigameScene');
        if (!scene) {
            infoElement.textContent = 'Scene not ready';
            return;
        }
        
        try {
            const texture = scene.textures.get(atlasKey);
            if (!texture) {
                infoElement.textContent = 'Atlas not found: ' + atlasKey;
                return;
            }
            
            const frame = texture.get(frameKey);
            if (!frame) {
                infoElement.textContent = 'Frame not found: ' + frameKey;
                return;
            }
            
            // Clear previous debug sprites
            this.clearDebugSprites();
            
            // Create sprite in the center of the game area
            const centerX = scene.cameras.main.centerX;
            const centerY = scene.cameras.main.centerY;
            
            const debugSprite = scene.add.sprite(centerX, centerY, atlasKey, frameKey)
                .setOrigin(0.5)
                .setDepth(9999) // Very high depth to appear on top
                .setScale(3); // Make it large and visible
            
            // Add a background to make it stand out
            const bg = scene.add.graphics()
                .fillStyle(0x000000, 0.8)
                .fillRoundedRect(
                    centerX - (frame.width * 3) / 2 - 20,
                    centerY - (frame.height * 3) / 2 - 20,
                    frame.width * 3 + 40,
                    frame.height * 3 + 40,
                    10
                )
                .setDepth(9998);
            
            // Add border
            const border = scene.add.graphics()
                .lineStyle(2, 0x4CAF50, 1)
                .strokeRoundedRect(
                    centerX - (frame.width * 3) / 2 - 20,
                    centerY - (frame.height * 3) / 2 - 20,
                    frame.width * 3 + 40,
                    frame.height * 3 + 40,
                    10
                )
                .setDepth(10000);
            
            // Add label text
            const label = scene.add.text(centerX, centerY + (frame.height * 3) / 2 + 35, frameKey, {
                fontSize: '16px',
                fontFamily: 'Courier New',
                color: '#4CAF50',
                align: 'center',
                backgroundColor: '#000000',
                padding: { x: 8, y: 4 }
            }).setOrigin(0.5).setDepth(10001);
            
            // Store for cleanup
            this.debugSprites.push(debugSprite, bg, border, label);
            
            // No auto-clear - user controls when to clear sprites
            
            // Update info
            infoElement.innerHTML = `
                <strong>${frameKey}</strong><br>
                Size: ${frame.width}x${frame.height}<br>
                Position: ${frame.x}, ${frame.y}<br>
                <em>Rendered on game area (3x scale)</em><br>
                <small>Sprite stays visible until you click Clear</small>
            `;
} catch (error) {
            logger.error('[SPRITE RENDERER] Error rendering sprite:', error);
            infoElement.textContent = 'Error rendering sprite: ' + error.message;
        }
    }
    
    clearDebugSprites() {
        this.debugSprites.forEach(sprite => {
            if (sprite && sprite.destroy) {
                sprite.destroy();
            }
        });
        this.debugSprites = [];
    }
    
    resetUploadInterface() {
        // Reset main upload area
        const uploadArea = document.getElementById('uploadArea');
        if (uploadArea) {
            uploadArea.innerHTML = `
                <div class="upload-icon">📁</div>
                <p>Click to select or drag & drop a spritesheet image</p>
                <small>Supported formats: PNG, JPG, JPEG, GIF</small>
            `;
        }
        
        // Reset main upload info
        const uploadInfo = document.getElementById('uploadInfo');
        if (uploadInfo) {
            uploadInfo.classList.add('hidden');
        }
        
        // Reset small upload area
        const uploadAreaSmall = document.getElementById('uploadAreaSmall');
        if (uploadAreaSmall) {
            uploadAreaSmall.innerHTML = `
                <div class="upload-prompt-small">
                    <span>📁 Click to upload spritesheet</span>
                </div>
            `;
        }
        
        // Reset small upload info
        const uploadInfoSmall = document.getElementById('uploadInfoSmall');
        if (uploadInfoSmall) {
            uploadInfoSmall.classList.add('hidden');
        }
        
        // Reset atlas dropdown and remove textures from Phaser
        const spriteAtlasSelect = document.getElementById('spriteAtlas');
        if (spriteAtlasSelect) {
            // Remove uploaded options and textures (keep only the default)
            const options = spriteAtlasSelect.querySelectorAll('option');
            options.forEach(option => {
                if (option.value && option.value.startsWith('uploaded_')) {
                    // Remove texture from Phaser
                    const scene = game.scene.getScene('MinigameScene');
                    if (scene && scene.textures.exists(option.value)) {
                        scene.textures.remove(option.value);
}
                    option.remove();
                }
            });
            spriteAtlasSelect.value = '';
        }
        
        // Reset frame dropdown
        const spriteFrameSelect = document.getElementById('spriteFrame');
        if (spriteFrameSelect) {
            spriteFrameSelect.innerHTML = '<option value="">Select Frame</option>';
        }
        
        // Reset file inputs
        const fileInputMain = document.getElementById('spritesheetFile');
        if (fileInputMain) {
            fileInputMain.value = '';
        }
        
        const fileInputSmall = document.getElementById('spritesheetFileSmall');
        if (fileInputSmall) {
            fileInputSmall.value = '';
        }
        
        // Show upload panel and hide sprite tools
        const uploadPanel = document.getElementById('uploadPanel');
        if (uploadPanel) {
            uploadPanel.style.display = 'block';
        }
        
        const spriteToolsPanel = document.getElementById('spriteToolsPanel');
        if (spriteToolsPanel) {
            spriteToolsPanel.classList.add('hidden');
        }
}
    
    openSpriteEditor(atlasKey) {
// CRITICAL: Block any calls during initialization
        if (this.isInitializing) {
            logger.error('[SPRITE EDITOR] Blocked auto-opening during initialization!');
            return;
        }
        
        // Safeguard: Don't open if no atlas key provided
        if (!atlasKey) {
            logger.error('[SPRITE EDITOR] No atlas key provided, aborting');
            return;
        }
        
        // FIRST: Immediately hide the old modal before doing anything else
        const existingModal = document.getElementById('spriteEditorModal');
        if (existingModal) {
            existingModal.style.display = 'none !important';
            existingModal.style.visibility = 'hidden !important';
            existingModal.style.opacity = '0 !important';
            existingModal.classList.add('hidden');
}
        
        // Hide the game container and show full-screen sprite editor
        const gameContainer = document.getElementById('gameContainer');
        gameContainer.style.display = 'none';
        
        // Remove any existing frameList elements to prevent conflicts
        const existingFrameLists = document.querySelectorAll('#frameList');
        existingFrameLists.forEach((element, index) => {
            if (index > 0) { // Keep the first one, remove duplicates
                element.remove();
            }
        });
        
        // Create full-screen sprite editor interface
        this.createFullScreenSpriteEditor(atlasKey);
        
        // Disable game input while editor is open
        this.disableGameInput();
}
    
    createFullScreenSpriteEditor(atlasKey) {
        // Create a simple, bulletproof full-screen editor
        const editor = document.createElement('div');
        editor.id = 'fullScreenSpriteEditor';
        editor.style.cssText = `
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            background: #1a1a1a !important;
            color: white !important;
            z-index: 999999 !important;
            font-family: Arial, sans-serif !important;
        `;
        
        // Create header bar
        const header = document.createElement('div');
        header.style.cssText = `
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 60px !important;
            background: rgba(0, 0, 0, 0.9) !important;
            border-bottom: 2px solid #4CAF50 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            padding: 0 20px !important;
            z-index: 1000001 !important;
        `;
        
        // Create title
        const title = document.createElement('h2');
        title.textContent = 'Sprite Editor - ' + atlasKey;
        title.style.cssText = `
            margin: 0 !important;
            color: #4CAF50 !important;
            font-size: 18px !important;
        `;
        
        // Create controls container
        const controls = document.createElement('div');
        controls.style.cssText = `
            display: flex !important;
            gap: 10px !important;
            align-items: center !important;
        `;
        
        // Create zoom controls
        const zoomLabel = document.createElement('label');
        zoomLabel.textContent = 'Zoom:';
        zoomLabel.style.cssText = `
            color: #ccc !important;
            font-size: 14px !important;
        `;
        
        const zoomSelect = document.createElement('select');
        zoomSelect.id = 'zoomSelect';
        zoomSelect.innerHTML = `
            <option value="0.5">50%</option>
            <option value="1" selected>100%</option>
            <option value="2">200%</option>
            <option value="4">400%</option>
            <option value="8">800%</option>
        `;
        zoomSelect.style.cssText = `
            background: rgba(255,255,255,0.1) !important;
            border: 1px solid #ccc !important;
            color: white !important;
            padding: 6px !important;
            border-radius: 4px !important;
        `;
        
        const fitToViewBtn = document.createElement('button');
        fitToViewBtn.id = 'fitToView';
        fitToViewBtn.textContent = 'Fit to View';
        fitToViewBtn.style.cssText = `
            background: #4CAF50 !important;
            border: none !important;
            color: white !important;
            padding: 8px 12px !important;
            border-radius: 4px !important;
            cursor: pointer !important;
            font-size: 12px !important;
        `;
        
        const toggleGridBtn = document.createElement('button');
        toggleGridBtn.id = 'toggleGrid';
        toggleGridBtn.textContent = 'Toggle Grid';
        toggleGridBtn.style.cssText = `
            background: rgba(255,255,255,0.1) !important;
            border: 1px solid #ccc !important;
            color: white !important;
            padding: 8px 12px !important;
            border-radius: 4px !important;
            cursor: pointer !important;
            font-size: 12px !important;
        `;
        
        // Create close button
        const closeBtn = document.createElement('button');
        closeBtn.id = 'closeSpriteEditor';
        closeBtn.textContent = '✕ Close Editor';
        closeBtn.style.cssText = `
            background: #dc3545 !important;
            color: white !important;
            border: none !important;
            padding: 10px 15px !important;
            border-radius: 4px !important;
            cursor: pointer !important;
            font-size: 14px !important;
        `;
        
        // Create frame editing controls for header
        const frameEditingControls = document.createElement('div');
        frameEditingControls.id = 'frameEditingControls';
        frameEditingControls.style.cssText = `
            display: flex !important;
            align-items: center !important;
            gap: 8px !important;
            margin: 0 20px !important;
        `;
        
        // Frame name input
        const frameNameInput = document.createElement('input');
        frameNameInput.id = 'frameNameInput';
        frameNameInput.placeholder = 'Frame name';
        frameNameInput.style.cssText = `
            background: rgba(0,0,0,0.8) !important;
            border: 1px solid #4CAF50 !important;
            color: white !important;
            padding: 6px 8px !important;
            border-radius: 4px !important;
            font-size: 12px !important;
            width: 150px !important;
        `;
        
        // Update frame button
        const updateFrameBtn = document.createElement('button');
        updateFrameBtn.id = 'updateFrame';
        updateFrameBtn.textContent = 'Update Frame';
        updateFrameBtn.style.cssText = `
            background: #4CAF50 !important;
            color: white !important;
            border: none !important;
            padding: 6px 12px !important;
            border-radius: 4px !important;
            cursor: pointer !important;
            font-size: 12px !important;
        `;
        
        // Export JSON button
        const exportJSONBtn = document.createElement('button');
        exportJSONBtn.id = 'exportJSON';
        exportJSONBtn.textContent = 'Export JSON';
        exportJSONBtn.style.cssText = `
            background: #ff9800 !important;
            color: white !important;
            border: none !important;
            padding: 6px 12px !important;
            border-radius: 4px !important;
            cursor: pointer !important;
            font-size: 12px !important;
        `;
        
        // Lock/Unlock button
        const lockBtn = document.createElement('button');
        lockBtn.id = 'lockButton';
        lockBtn.textContent = '🔓 Unlocked';
        lockBtn.style.cssText = `
            background: #28a745 !important;
            color: white !important;
            border: none !important;
            padding: 6px 12px !important;
            border-radius: 4px !important;
            cursor: pointer !important;
            font-size: 12px !important;
            transition: all 0.2s ease !important;
        `;
        
        frameEditingControls.appendChild(frameNameInput);
        frameEditingControls.appendChild(updateFrameBtn);
        frameEditingControls.appendChild(exportJSONBtn);
        frameEditingControls.appendChild(lockBtn);

        // Assemble header
        controls.appendChild(zoomLabel);
        controls.appendChild(zoomSelect);
        controls.appendChild(fitToViewBtn);
        controls.appendChild(toggleGridBtn);
        controls.appendChild(closeBtn);
        
        header.appendChild(title);
        header.appendChild(frameEditingControls);
        header.appendChild(controls);
        
        // Create frame list
        const frameList = document.createElement('div');
        frameList.id = 'frameList';
        frameList.style.cssText = `
            position: absolute !important;
            top: 60px !important;
            left: 10px !important;
            width: 200px !important;
            height: calc(100vh - 60px) !important;
            background: rgba(0,0,0,0.8) !important;
            border: 1px solid #4CAF50 !important;
            overflow-y: auto !important;
            padding: 10px !important;
            box-sizing: border-box !important;
        `;
        
        // Create canvas - calculate size to fill available space
        const canvas = document.createElement('canvas');
        canvas.id = 'spritesheetCanvas';
        
        // Calculate available space
        // Screen width - sidebar width - padding
        const availableWidth = window.innerWidth - 220 - 20; // 220px sidebar + 20px padding
        // Screen height - header - coordinate display - padding  
        const availableHeight = window.innerHeight - 60 - 200 - 20; // 60px header + 200px coord space + 20px padding
        
        canvas.width = availableWidth;
        canvas.height = availableHeight;
        canvas.style.cssText = `
            position: absolute !important;
            top: 70px !important;
            left: 230px !important;
            width: ${availableWidth}px !important;
            height: ${availableHeight}px !important;
            border: 2px solid #4CAF50 !important;
            background: #000 !important;
            image-rendering: pixelated !important;
            display: block !important;
            visibility: visible !important;
        `;
// Test canvas immediately
        const testCtx = canvas.getContext('2d');
        if (testCtx) {
            testCtx.fillStyle = 'yellow';
            testCtx.fillRect(0, 0, 100, 100);
        } else {
            logger.error('[SPRITE EDITOR] Failed to get canvas context!');
        }
        
        // Create coordinate display
        const coordDisplay = document.createElement('div');
        coordDisplay.id = 'coordDisplay';
        coordDisplay.textContent = 'Click and drag to select area';
        const coordTop = 70 + availableHeight + 10; // Below canvas with margin
        coordDisplay.style.cssText = `
            position: absolute !important;
            top: ${coordTop}px !important;
            left: 230px !important;
            width: ${availableWidth - 20}px !important;
            color: #4CAF50 !important;
            background: rgba(0,0,0,0.8) !important;
            padding: 10px !important;
            border-radius: 8px !important;
            font-family: monospace !important;
            background: rgba(0,0,0,0.8) !important;
            padding: 10px !important;
            border: 1px solid #4CAF50 !important;
            border-radius: 4px !important;
        `;
        
        // Set up event handlers for header buttons
        exportJSONBtn.onclick = () => {
            this.exportAtlasJSON();
        };

        updateFrameBtn.onclick = () => {
            this.updateFrameFromInput();
        };
        
        lockBtn.onclick = () => {
            this.toggleLock();
        };
        
        // Assemble the editor
        editor.appendChild(header);
        editor.appendChild(frameList);
        editor.appendChild(canvas);
        editor.appendChild(coordDisplay);
        
        // Add to body
        document.body.appendChild(editor);
// Test canvas again after adding to DOM
        setTimeout(() => {
            const domCanvas = document.getElementById('spritesheetCanvas');
            if (domCanvas) {
                const domCtx = domCanvas.getContext('2d');
                if (domCtx) {
                    domCtx.fillStyle = 'blue';
                    domCtx.fillRect(10, 10, 50, 50);
                } else {
                    logger.error('[SPRITE EDITOR] Cannot get context from DOM canvas');
                }
            } else {
                logger.error('[SPRITE EDITOR] Canvas not found in DOM!');
            }
        }, 100);
        
        // Set up close button immediately
        closeBtn.onclick = () => {
            this.closeFullScreenEditor();
        };

        // Set up zoom controls
        zoomSelect.onchange = (e) => {
            const zoomLevel = parseFloat(e.target.value);
            this.applyZoomToCanvas(zoomLevel);
        };

        fitToViewBtn.onclick = () => {
            zoomSelect.value = '1';
            this.applyZoomToCanvas(1.0);
        };

        toggleGridBtn.onclick = () => {
            this.gridVisible = !this.gridVisible;
            this.redrawCanvasWithCurrentFrame(atlasKey);
        };
        
        // Initialize immediately with error handling
        try {
            this.initializeFullScreenEditor(atlasKey);
        } catch (error) {
            logger.error('[SPRITE EDITOR] Error during initialization:', error);
        }
    }
    
    initializeFullScreenEditor(atlasKey) {
// Initialize editor properties
        this.currentAtlas = atlasKey;
        this.gridVisible = false;
        this.currentZoomLevel = 1.0;
        this.selectedFrame = null;
        this.dragState = {
            isDragging: false,
            isPanning: false,
            isCreatingFrame: false,
            isDuplicating: false,
            startX: 0,
            startY: 0,
            dragHandle: null, // 'move', 'resize-tl', 'resize-tr', 'resize-bl', 'resize-br', etc.
            originalFrame: null,
            duplicateSourceFrame: null
        };
        this.panOffset = { x: 0, y: 0 };
        this.snapToPixel = true;
        this.lockedFrames = new Set(); // Set of locked frame names
        
        // Undo/Redo system
        this.actionHistory = [];
        this.historyIndex = -1;
        this.maxHistorySize = 50;
        
        const canvas = document.getElementById('spritesheetCanvas');
        if (!canvas) {
            logger.error('[SPRITE EDITOR] Canvas element not found!');
            return;
        }
        
        const ctx = canvas.getContext('2d');
        
        // Load and display the spritesheet
        const scene = game.scene.getScene('MinigameScene');
        if (!scene) {
            logger.error('[SPRITE EDITOR] Scene not found!');
            return;
        }
        
        try {
            const texture = scene.textures.get(atlasKey);
            if (!texture) {
                logger.error('[SPRITE EDITOR] Texture not found for atlas:', atlasKey);
                return;
            }
            const sourceImage = texture.source[0].image;
            if (!sourceImage) {
                logger.error('[SPRITE EDITOR] Source image not found in texture');
                return;
            }
            // Keep the canvas at a fixed, reasonable size and scale the image to fit
            // Force canvas to be visible with a red background first
            ctx.fillStyle = 'red';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            // Wait a moment to see red, then draw the image
            setTimeout(() => {
                // Calculate scale to fit image in canvas
                const scaleX = canvas.width / sourceImage.width;
                const scaleY = canvas.height / sourceImage.height;
                const scale = Math.min(scaleX, scaleY) * 0.9; // 90% to leave some margin
                
                const scaledWidth = sourceImage.width * scale;
                const scaledHeight = sourceImage.height * scale;
                const x = (canvas.width - scaledWidth) / 2;
                const y = (canvas.height - scaledHeight) / 2;
                // Clear and draw background
                ctx.fillStyle = '#333';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Draw the spritesheet image scaled and centered
                ctx.imageSmoothingEnabled = false;
                ctx.drawImage(sourceImage, x, y, scaledWidth, scaledHeight);
                // Draw frame outlines scaled
                this.drawFrameOutlinesScaled(ctx, texture, scale, x, y);
            }, 500); // 500ms delay to see red background
            
            // Populate frame list
            this.populateFrameList(texture, atlasKey);
            
            // Add interactive mouse handlers for frame editing
            this.setupInteractiveHandlers(canvas);
            
        } catch (error) {
            logger.error('[SPRITE EDITOR] Error loading spritesheet:', error);
        }
    }
    
    setupInteractiveHandlers(canvas) {
        // Remove any existing handlers
        canvas.onmousedown = null;
        canvas.onmousemove = null;
        canvas.onmouseup = null;
        canvas.onwheel = null;
        
        // Mouse down - start interaction
        canvas.onmousedown = (e) => {
            e.preventDefault();
            const rect = canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            if (e.button === 2) { // Right click
                this.showContextMenu(e, mouseX, mouseY);
                return;
            }
            
            if (e.button === 1 || (e.button === 0 && e.shiftKey)) { // Middle click or Shift+Left click
                // Start new frame creation
                this.dragState.isCreatingFrame = true;
                this.dragState.startX = mouseX;
                this.dragState.startY = mouseY;
                this.selectedFrame = null;
                canvas.style.cursor = 'crosshair';
                return;
            }
            
            if (e.ctrlKey && e.button === 0) { // Ctrl + Left click
                // Start panning
                this.dragState.isPanning = true;
                this.dragState.startX = mouseX;
                this.dragState.startY = mouseY;
                canvas.style.cursor = 'grabbing';
                return;
            }
            
            if (e.button === 0) { // Left click
                // Check for frame interaction
                const interaction = this.getFrameInteraction(mouseX, mouseY);
                if (interaction) {
                    // Check if this specific frame is locked
                    if (this.isFrameLocked(interaction.frameName)) {
                        return;
                    }

                    // Check for Alt+drag to duplicate frame
                    if (e.altKey && interaction.handle === 'move') {
                        this.startFrameDuplication(interaction, mouseX, mouseY);
                        return;
                    }
                    try {
                        // Save state for undo before starting drag
                        this.saveStateToHistory('frame_modify', interaction.frameName);
                        
                        this.dragState.isDragging = true;
                        this.dragState.startX = mouseX;
                        this.dragState.startY = mouseY;
                        this.dragState.dragHandle = interaction.handle;
                        // Create a deep copy of the frame data
                        this.dragState.originalFrame = {
                            cutX: interaction.frame.cutX || interaction.frame.x || 0,
                            cutY: interaction.frame.cutY || interaction.frame.y || 0,
                            cutWidth: interaction.frame.cutWidth || interaction.frame.width || 0,
                            cutHeight: interaction.frame.cutHeight || interaction.frame.height || 0,
                            x: interaction.frame.cutX || interaction.frame.x || 0,
                            y: interaction.frame.cutY || interaction.frame.y || 0,
                            width: interaction.frame.cutWidth || interaction.frame.width || 0,
                            height: interaction.frame.cutHeight || interaction.frame.height || 0
                        };
                        this.selectedFrame = interaction.frameName;

                        // Update frame list selection
                        this.updateFrameListSelection(interaction.frameName);

                        // Set appropriate cursor
                        canvas.style.cursor = this.getCursorForHandle(interaction.handle);
                    } catch (error) {
                        logger.error('[ERROR] Failed to start drag:', error);
                    }
                } else {
                    // Click on empty space - just deselect
                    this.selectedFrame = null;
                    
                    // Update lock button state for no selection
                    this.updateFrameListSelection(null);
                    
                    // Clear frame list selection and coordinate display
                    const fullScreenEditor = document.getElementById('fullScreenSpriteEditor');
                    const frameList = fullScreenEditor ? fullScreenEditor.querySelector('#frameList') : null;
                    if (frameList) {
                        frameList.querySelectorAll('.frame-item').forEach(item => {
                            item.classList.remove('active');
                            item.style.background = 'rgba(255, 255, 255, 0.1)';
                            item.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                            item.style.color = '#ccc';
                        });
                    }
                    
                    const coordDisplay = document.getElementById('coordDisplay');
                    if (coordDisplay) {
                        coordDisplay.innerHTML = `
                            <div style="background: rgba(255,255,255,0.1); padding: 8px; border-radius: 4px; text-align: center; color: #999;">
                                No frame selected. Click a frame to edit it.
                            </div>
                        `;
                    }
                    
                    document.getElementById('frameNameInput').value = '';
                    this.currentSelection = null;
                    
                    this.redrawCanvasWithCurrentFrame(this.currentAtlas);
                }
            }
        };
        
        // Mouse move - handle dragging/panning
        canvas.onmousemove = (e) => {
            const rect = canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            
            
            if (this.dragState.isPanning) {
                // Handle panning
                const deltaX = mouseX - this.dragState.startX;
                const deltaY = mouseY - this.dragState.startY;
                this.panOffset.x += deltaX;
                this.panOffset.y += deltaY;
                this.dragState.startX = mouseX;
                this.dragState.startY = mouseY;
                this.redrawCanvasWithCurrentFrame(this.currentAtlas);
                return;
            }
            
            if (this.dragState.isCreatingFrame) {
                // Handle new frame creation
                this.drawSelectionRectangle(this.dragState.startX, this.dragState.startY, 
                    mouseX - this.dragState.startX, mouseY - this.dragState.startY);
                return;
            }
            
            if (this.dragState.isDuplicating) {
                // Handle frame duplication visual feedback
                this.drawDuplicationPreview(mouseX, mouseY);
                return;
            }
            
            if (this.dragState.isDragging) {
                this.handleFrameDrag(mouseX, mouseY);
            } else {
                // Update cursor based on hover
                const interaction = this.getFrameInteraction(mouseX, mouseY);
                if (interaction) {
                    // Show copy cursor when Alt is held over a frame's move handle
                    if (e.altKey && interaction.handle === 'move') {
                        canvas.style.cursor = 'copy';
                    } else {
                        canvas.style.cursor = this.getCursorForHandle(interaction.handle);
                    }
                } else if (e.ctrlKey) {
                    canvas.style.cursor = 'grab';
                } else if (e.shiftKey || e.button === 1) {
                    canvas.style.cursor = 'crosshair';
                } else {
                    canvas.style.cursor = 'default';
                }
            }
        };
        
        // Mouse up - end interaction
        canvas.onmouseup = (e) => {
            const rect = canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            
            if (this.dragState.isPanning) {
                this.dragState.isPanning = false;
                canvas.style.cursor = e.ctrlKey ? 'grab' : 'default';
            }
            
            if (this.dragState.isCreatingFrame) {
                this.finishFrameCreation(mouseX, mouseY);
                this.dragState.isCreatingFrame = false;
                canvas.style.cursor = 'default';
            }
            
            if (this.dragState.isDuplicating) {
                this.finishFrameDuplication(mouseX, mouseY);
                this.dragState.isDuplicating = false;
                this.dragState.duplicateSourceFrame = null;
                canvas.style.cursor = 'default';
            }
            
            if (this.dragState.isDragging) {
                this.finishFrameDrag();
                this.dragState.isDragging = false;
                this.dragState.dragHandle = null;
                this.dragState.originalFrame = null;
                canvas.style.cursor = 'default';
            }
        };
        
        // Mouse wheel - zoom
        canvas.onwheel = (e) => {
            e.preventDefault();
            const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
            const newZoom = Math.max(0.1, Math.min(8.0, this.currentZoomLevel * zoomFactor));
            
            // Update zoom select
            const zoomSelect = document.getElementById('fullScreenSpriteEditor').querySelector('select');
            if (zoomSelect) {
                zoomSelect.value = newZoom.toString();
            }
            
            this.applyZoomToCanvas(newZoom);
        };
        
        // Prevent context menu on canvas for right-click handling
        canvas.oncontextmenu = (e) => {
            e.preventDefault();
            return false;
        };
        
        // Keyboard handlers
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                // Deselect current frame
                this.selectedFrame = null;
                
                // Clear frame list selection and coordinate display
                const fullScreenEditor = document.getElementById('fullScreenSpriteEditor');
                const frameList = fullScreenEditor ? fullScreenEditor.querySelector('#frameList') : null;
                if (frameList) {
                    frameList.querySelectorAll('.frame-item').forEach(item => {
                        item.classList.remove('active');
                        item.style.background = 'rgba(255, 255, 255, 0.1)';
                        item.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        item.style.color = '#ccc';
                    });
                }
                
                const coordDisplay = document.getElementById('coordDisplay');
                if (coordDisplay) {
                    coordDisplay.innerHTML = `
                        <div style="background: rgba(255,255,255,0.1); padding: 8px; border-radius: 4px; text-align: center; color: #999;">
                            No frame selected. Click a frame to edit it.
                        </div>
                    `;
                }
                
                document.getElementById('frameNameInput').value = '';
                this.currentSelection = null;
                
                this.redrawCanvasWithCurrentFrame(this.currentAtlas);
            }
            if (e.key === 'g' || e.key === 'G') {
                this.gridVisible = !this.gridVisible;
                this.redrawCanvasWithCurrentFrame(this.currentAtlas);
            }
            if (e.key === '?' || e.key === 'h' || e.key === 'H') {
                this.showHelpDialog();
            }
            if (e.key === 'Delete' && this.selectedFrame) {
                this.deleteSelectedFrame();
            }
            if (e.ctrlKey && e.key === 'z') {
                e.preventDefault();
                this.undo();
            }
            if (e.ctrlKey && e.key === 'y') {
                e.preventDefault();
                this.redo();
            }

            // L key to toggle lock for selected frame
            if ((e.key === 'l' || e.key === 'L') && this.selectedFrame) {
                e.preventDefault();
                this.toggleLock();
            }
            
            // Arrow key movement for selected frames
            if (this.selectedFrame && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                e.preventDefault();
                this.moveSelectedFrame(e.key);
            }
        });
    }
    
    getFrameInteraction(mouseX, mouseY) {
        const canvas = document.getElementById('spritesheetCanvas');
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(this.currentAtlas);
        const sourceImage = texture.source[0].image;
        
        // Calculate scale and positioning
        const baseScaleX = canvas.width / sourceImage.width;
        const baseScaleY = canvas.height / sourceImage.height;
        const baseScale = Math.min(baseScaleX, baseScaleY) * 0.9;
        const scale = baseScale * this.currentZoomLevel;
        
        const scaledWidth = sourceImage.width * scale;
        const scaledHeight = sourceImage.height * scale;
        const offsetX = (canvas.width - scaledWidth) / 2 + this.panOffset.x;
        const offsetY = (canvas.height - scaledHeight) / 2 + this.panOffset.y;
        
        // Check each frame
        for (const frameName of Object.keys(texture.frames)) {
            if (frameName === '__BASE') continue;
            
            const frame = texture.frames[frameName];
            const frameX = (frame.cutX || frame.x || 0) * scale + offsetX;
            const frameY = (frame.cutY || frame.y || 0) * scale + offsetY;
            const frameWidth = (frame.cutWidth || frame.width || 0) * scale;
            const frameHeight = (frame.cutHeight || frame.height || 0) * scale;
            
            // Check for resize handles (corners and edges)
            const handleSize = 8;
            const handles = [
                { name: 'resize-tl', x: frameX - handleSize/2, y: frameY - handleSize/2, w: handleSize, h: handleSize },
                { name: 'resize-tr', x: frameX + frameWidth - handleSize/2, y: frameY - handleSize/2, w: handleSize, h: handleSize },
                { name: 'resize-bl', x: frameX - handleSize/2, y: frameY + frameHeight - handleSize/2, w: handleSize, h: handleSize },
                { name: 'resize-br', x: frameX + frameWidth - handleSize/2, y: frameY + frameHeight - handleSize/2, w: handleSize, h: handleSize },
                { name: 'resize-t', x: frameX + frameWidth/2 - handleSize/2, y: frameY - handleSize/2, w: handleSize, h: handleSize },
                { name: 'resize-b', x: frameX + frameWidth/2 - handleSize/2, y: frameY + frameHeight - handleSize/2, w: handleSize, h: handleSize },
                { name: 'resize-l', x: frameX - handleSize/2, y: frameY + frameHeight/2 - handleSize/2, w: handleSize, h: handleSize },
                { name: 'resize-r', x: frameX + frameWidth - handleSize/2, y: frameY + frameHeight/2 - handleSize/2, w: handleSize, h: handleSize }
            ];
            
            // Check handles first (higher priority)
            for (const handle of handles) {
                if (mouseX >= handle.x && mouseX <= handle.x + handle.w &&
                    mouseY >= handle.y && mouseY <= handle.y + handle.h) {
                    return {
                        frameName,
                        frame: frame,
                        handle: handle.name,
                        frameX, frameY, frameWidth, frameHeight
                    };
                }
            }
            
            // Check frame body for move
            if (mouseX >= frameX && mouseX <= frameX + frameWidth &&
                mouseY >= frameY && mouseY <= frameY + frameHeight) {
                return {
                    frameName,
                    frame: frame,
                    handle: 'move',
                    frameX, frameY, frameWidth, frameHeight
                };
            }
        }
        
        return null;
    }
    
    getCursorForHandle(handle) {
        const cursors = {
            'move': 'move',
            'resize-tl': 'nw-resize',
            'resize-tr': 'ne-resize',
            'resize-bl': 'sw-resize',
            'resize-br': 'se-resize',
            'resize-t': 'n-resize',
            'resize-b': 's-resize',
            'resize-l': 'w-resize',
            'resize-r': 'e-resize',
            'create': 'crosshair'
        };
        return cursors[handle] || 'default';
    }
    
    handleFrameDrag(mouseX, mouseY) {
        if (!this.dragState.isDragging) return;
        
        const deltaX = mouseX - this.dragState.startX;
        const deltaY = mouseY - this.dragState.startY;
        
        // Handle existing frame modification only
        this.modifyFrame(deltaX, deltaY);
    }
    
    modifyFrame(deltaX, deltaY) {
        const canvas = document.getElementById('spritesheetCanvas');
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(this.currentAtlas);
        const sourceImage = texture.source[0].image;
        
        // Calculate scale
        const baseScaleX = canvas.width / sourceImage.width;
        const baseScaleY = canvas.height / sourceImage.height;
        const baseScale = Math.min(baseScaleX, baseScaleY) * 0.9;
        const scale = baseScale * this.currentZoomLevel;
        
        // Convert screen delta to texture coordinates
        const textureDeltaX = deltaX / scale;
        const textureDeltaY = deltaY / scale;
        
        const frame = texture.frames[this.selectedFrame];
        const original = this.dragState.originalFrame;
        
        let newX = original.cutX || original.x || 0;
        let newY = original.cutY || original.y || 0;
        let newWidth = original.cutWidth || original.width || 0;
        let newHeight = original.cutHeight || original.height || 0;
        
        // Apply modification based on handle type
        switch (this.dragState.dragHandle) {
            case 'move':
                newX = original.cutX + textureDeltaX;
                newY = original.cutY + textureDeltaY;
                break;
            case 'resize-tl':
                newX = original.cutX + textureDeltaX;
                newY = original.cutY + textureDeltaY;
                newWidth = original.cutWidth - textureDeltaX;
                newHeight = original.cutHeight - textureDeltaY;
                break;
            case 'resize-tr':
                newY = original.cutY + textureDeltaY;
                newWidth = original.cutWidth + textureDeltaX;
                newHeight = original.cutHeight - textureDeltaY;
                break;
            case 'resize-bl':
                newX = original.cutX + textureDeltaX;
                newWidth = original.cutWidth - textureDeltaX;
                newHeight = original.cutHeight + textureDeltaY;
                break;
            case 'resize-br':
                newWidth = original.cutWidth + textureDeltaX;
                newHeight = original.cutHeight + textureDeltaY;
                break;
            case 'resize-t':
                newY = original.cutY + textureDeltaY;
                newHeight = original.cutHeight - textureDeltaY;
                break;
            case 'resize-b':
                newHeight = original.cutHeight + textureDeltaY;
                break;
            case 'resize-l':
                newX = original.cutX + textureDeltaX;
                newWidth = original.cutWidth - textureDeltaX;
                break;
            case 'resize-r':
                newWidth = original.cutWidth + textureDeltaX;
                break;
        }
        
        // Apply pixel snapping
        if (this.snapToPixel) {
            newX = Math.round(newX);
            newY = Math.round(newY);
            newWidth = Math.round(newWidth);
            newHeight = Math.round(newHeight);
        }
        
        // Ensure minimum size
        newWidth = Math.max(1, newWidth);
        newHeight = Math.max(1, newHeight);
        
        // Ensure bounds
        newX = Math.max(0, Math.min(sourceImage.width - newWidth, newX));
        newY = Math.max(0, Math.min(sourceImage.height - newHeight, newY));
        
        // Update frame data temporarily (will be committed on mouse up)
        frame.cutX = newX;
        frame.cutY = newY;
        frame.cutWidth = newWidth;
        frame.cutHeight = newHeight;
        frame.x = newX;
        frame.y = newY;
        frame.width = newWidth;
        frame.height = newHeight;
        
        // Redraw with updated frame
        this.redrawCanvasWithCurrentFrame(this.currentAtlas);
        
        // Update coordinate display
        const coordDisplay = document.getElementById('coordDisplay');
        if (coordDisplay) {
            coordDisplay.textContent = `${this.selectedFrame}: x=${newX}, y=${newY}, w=${newWidth}, h=${newHeight}`;
        }
    }
    
    drawSelectionRectangle(startX, startY, deltaX, deltaY) {
        // Redraw canvas and add selection rectangle
        this.redrawCanvasWithCurrentFrame(this.currentAtlas);
        
        const canvas = document.getElementById('spritesheetCanvas');
        const ctx = canvas.getContext('2d');
        
        // Draw selection rectangle
        ctx.strokeStyle = '#00ff00';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(startX, startY, deltaX, deltaY);
        ctx.setLineDash([]);
    }
    
    finishFrameDrag() {
        if (this.selectedFrame) {
            // Frame modification completed - update the JSON data
}
    }
    
    finishFrameCreation(mouseX, mouseY) {
        const canvas = document.getElementById('spritesheetCanvas');
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(this.currentAtlas);
        const sourceImage = texture.source[0].image;
        
        // Calculate scale and position
        const baseScaleX = canvas.width / sourceImage.width;
        const baseScaleY = canvas.height / sourceImage.height;
        const baseScale = Math.min(baseScaleX, baseScaleY) * 0.9;
        const scale = baseScale * this.currentZoomLevel;
        
        const scaledWidth = sourceImage.width * scale;
        const scaledHeight = sourceImage.height * scale;
        const offsetX = (canvas.width - scaledWidth) / 2 + this.panOffset.x;
        const offsetY = (canvas.height - scaledHeight) / 2 + this.panOffset.y;

        // Convert screen coordinates to texture coordinates
        // Formula: textureCoord = (screenCoord - imageOffset) / scale
        // This reverses the display transformation to get actual pixel positions in the source image
        const textureStartX = (this.dragState.startX - offsetX) / scale;
        const textureStartY = (this.dragState.startY - offsetY) / scale;
        const textureEndX = (mouseX - offsetX) / scale;
        const textureEndY = (mouseY - offsetY) / scale;
        
        const newX = Math.min(textureStartX, textureEndX);
        const newY = Math.min(textureStartY, textureEndY);
        const newWidth = Math.abs(textureEndX - textureStartX);
        const newHeight = Math.abs(textureEndY - textureStartY);
        
        if (newWidth > 5 && newHeight > 5) { // Minimum size check
            // eslint-disable-next-line no-alert
            const frameName = prompt('Enter frame name:');
            if (frameName && frameName.trim()) {
                this.saveStateToHistory('frame_create', frameName);
                this.addNewFrame(frameName.trim(), newX, newY, newWidth, newHeight);
            }
        }
        
        // Redraw to clear selection rectangle
        this.redrawCanvasWithCurrentFrame(this.currentAtlas);
    }
    
    startFrameDuplication(interaction, mouseX, mouseY) {
// Store the original frame data for duplication
        this.dragState.isDuplicating = true;
        this.dragState.startX = mouseX;
        this.dragState.startY = mouseY;
        this.dragState.duplicateSourceFrame = {
            name: interaction.frameName,
            cutX: interaction.frame.cutX || interaction.frame.x || 0,
            cutY: interaction.frame.cutY || interaction.frame.y || 0,
            cutWidth: interaction.frame.cutWidth || interaction.frame.width || 0,
            cutHeight: interaction.frame.cutHeight || interaction.frame.height || 0,
            x: interaction.frame.cutX || interaction.frame.x || 0,
            y: interaction.frame.cutY || interaction.frame.y || 0,
            width: interaction.frame.cutWidth || interaction.frame.width || 0,
            height: interaction.frame.cutHeight || interaction.frame.height || 0
        };
        
        // Set cursor to indicate duplication mode
        const canvas = document.getElementById('spritesheetCanvas');
        canvas.style.cursor = 'copy';
        
        // Save state for undo
        this.saveStateToHistory('frame_duplicate', interaction.frameName);
    }
    
    finishFrameDuplication(mouseX, mouseY) {
        if (!this.dragState.isDuplicating || !this.dragState.duplicateSourceFrame) {
            return;
        }
        
        const canvas = document.getElementById('spritesheetCanvas');
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(this.currentAtlas);
        const sourceImage = texture.source[0].image;
        
        // Calculate scale and position
        const baseScaleX = canvas.width / sourceImage.width;
        const baseScaleY = canvas.height / sourceImage.height;
        const baseScale = Math.min(baseScaleX, baseScaleY) * 0.9;
        const scale = baseScale * this.currentZoomLevel;
        
        // Calculate drag distance in texture coordinates
        const textureDeltaX = (mouseX - this.dragState.startX) / scale;
        const textureDeltaY = (mouseY - this.dragState.startY) / scale;

        // Calculate new position for the duplicated frame
        const originalFrame = this.dragState.duplicateSourceFrame;
        const newX = originalFrame.cutX + textureDeltaX;
        const newY = originalFrame.cutY + textureDeltaY;
        
        // Ensure the new position is within bounds
        const boundedX = Math.max(0, Math.min(sourceImage.width - originalFrame.cutWidth, newX));
        const boundedY = Math.max(0, Math.min(sourceImage.height - originalFrame.cutHeight, newY));
        
        // Generate a new frame name
        const baseName = originalFrame.name;
        let newFrameName = baseName + '_copy';
        let counter = 1;
        
        // Find a unique name
        while (texture.frames[newFrameName]) {
            newFrameName = baseName + '_copy_' + counter;
            counter++;
        }
        
        // Prompt user for frame name with the suggested name
        // eslint-disable-next-line no-alert
        const userFrameName = prompt('Enter name for duplicated frame:', newFrameName);
        if (userFrameName && userFrameName.trim()) {
            const finalFrameName = userFrameName.trim();

            // Create the new frame
            this.addNewFrame(finalFrameName, boundedX, boundedY, originalFrame.cutWidth, originalFrame.cutHeight);

            // Select the new frame
            this.selectedFrame = finalFrameName;
            this.updateFrameListSelection(finalFrameName);
        }
        
        // Reset drag state
        this.dragState.isDuplicating = false;
        this.dragState.duplicateSourceFrame = null;
        canvas.style.cursor = 'default';
    }
    
    drawDuplicationPreview(mouseX, mouseY) {
        if (!this.dragState.duplicateSourceFrame) return;
        
        const canvas = document.getElementById('spritesheetCanvas');
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(this.currentAtlas);
        const sourceImage = texture.source[0].image;
        
        // Calculate scale and position
        const baseScaleX = canvas.width / sourceImage.width;
        const baseScaleY = canvas.height / sourceImage.height;
        const baseScale = Math.min(baseScaleX, baseScaleY) * 0.9;
        const scale = baseScale * this.currentZoomLevel;
        
        const scaledWidth = sourceImage.width * scale;
        const scaledHeight = sourceImage.height * scale;
        const offsetX = (canvas.width - scaledWidth) / 2 + this.panOffset.x;
        const offsetY = (canvas.height - scaledHeight) / 2 + this.panOffset.y;
        
        // Calculate drag distance in texture coordinates
        const textureDeltaX = (mouseX - this.dragState.startX) / scale;
        const textureDeltaY = (mouseY - this.dragState.startY) / scale;
        
        // Calculate new position for the preview
        const originalFrame = this.dragState.duplicateSourceFrame;
        const newX = originalFrame.cutX + textureDeltaX;
        const newY = originalFrame.cutY + textureDeltaY;
        
        // Redraw the canvas with current frame
        this.redrawCanvasWithCurrentFrame(this.currentAtlas);
        
        // Draw the duplication preview
        const ctx = canvas.getContext('2d');
        
        // Calculate screen position of the preview frame
        const screenX = newX * scale + offsetX;
        const screenY = newY * scale + offsetY;
        const screenWidth = originalFrame.cutWidth * scale;
        const screenHeight = originalFrame.cutHeight * scale;
        
        // Draw a dashed outline to show where the duplicate will be placed
        ctx.strokeStyle = '#ffff00'; // Yellow for duplication
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(screenX, screenY, screenWidth, screenHeight);
        ctx.setLineDash([]);
        
        // Draw a semi-transparent overlay
        ctx.fillStyle = 'rgba(255, 255, 0, 0.2)';
        ctx.fillRect(screenX, screenY, screenWidth, screenHeight);
        
        // Draw "COPY" text in the center
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('COPY', screenX + screenWidth / 2, screenY + screenHeight / 2);
    }
    
    createNewFrame() {
        const canvas = document.getElementById('spritesheetCanvas');
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(this.currentAtlas);
        const sourceImage = texture.source[0].image;
        
        // Calculate scale and position
        const baseScaleX = canvas.width / sourceImage.width;
        const baseScaleY = canvas.height / sourceImage.height;
        const baseScale = Math.min(baseScaleX, baseScaleY) * 0.9;
        const scale = baseScale * this.currentZoomLevel;
        
        const scaledWidth = sourceImage.width * scale;
        const scaledHeight = sourceImage.height * scale;
        const offsetX = (canvas.width - scaledWidth) / 2 + this.panOffset.x;
        const offsetY = (canvas.height - scaledHeight) / 2 + this.panOffset.y;
        
        // Convert screen coordinates to texture coordinates (need to get current mouse position)
        const rect = canvas.getBoundingClientRect();
        const currentMouseX = event ? event.clientX - rect.left : this.dragState.startX;
        const currentMouseY = event ? event.clientY - rect.top : this.dragState.startY;
        
        const textureStartX = (this.dragState.startX - offsetX) / scale;
        const textureStartY = (this.dragState.startY - offsetY) / scale;
        const textureEndX = (currentMouseX - offsetX) / scale;
        const textureEndY = (currentMouseY - offsetY) / scale;
        
        const newX = Math.min(textureStartX, textureEndX);
        const newY = Math.min(textureStartY, textureEndY);
        const newWidth = Math.abs(textureEndX - textureStartX);
        const newHeight = Math.abs(textureEndY - textureStartY);
        
        if (newWidth > 5 && newHeight > 5) { // Minimum size check
            // eslint-disable-next-line no-alert
            const frameName = prompt('Enter frame name:');
            if (frameName) {
                this.addNewFrame(frameName, newX, newY, newWidth, newHeight);
            }
        }
    }
    
    addNewFrame(frameName, x, y, width, height) {
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(this.currentAtlas);

        // Apply pixel snapping
        let finalX = x;
        let finalY = y;
        let finalWidth = width;
        let finalHeight = height;

        if (this.snapToPixel) {
            finalX = Math.round(x);
            finalY = Math.round(y);
            finalWidth = Math.round(width);
            finalHeight = Math.round(height);
        }

        // Add frame to texture
        texture.frames[frameName] = {
            cutX: finalX,
            cutY: finalY,
            cutWidth: finalWidth,
            cutHeight: finalHeight,
            x: finalX,
            y: finalY,
            width: finalWidth,
            height: finalHeight,
            rotated: false,
            trimmed: false,
            spriteSourceSize: { x: 0, y: 0, w: finalWidth, h: finalHeight },
            sourceSize: { w: finalWidth, h: finalHeight }
        };

        // Refresh frame list and canvas
        this.populateFrameList(texture, this.currentAtlas);
        this.selectedFrame = frameName;
        this.updateFrameListSelection(frameName);
        this.redrawCanvasWithCurrentFrame(this.currentAtlas);
    }
    
    exportAtlasJSON() {
        try {
            const scene = game.scene.getScene('MinigameScene');
            if (!scene) {
                logger.error('[SPRITE EDITOR] No scene found');
                // eslint-disable-next-line no-alert
                alert('Error: Game scene not found');
                return;
            }

            const texture = scene.textures.get(this.currentAtlas);
            if (!texture) {
                logger.error('[SPRITE EDITOR] No texture found for atlas:', this.currentAtlas);
                // eslint-disable-next-line no-alert
                alert('Error: Texture not found');
                return;
            }

            const sourceImage = texture.source[0].image;
            if (!sourceImage) {
                logger.error('[SPRITE EDITOR] No source image found');
                // eslint-disable-next-line no-alert
                alert('Error: Source image not found');
                return;
            }
            // Build the JSON structure
            const exportData = {
            frames: [],
            meta: {
                version: "1.0",
                image: `${this.currentAtlas.replace('Atlas', '')}-spritesheet.png`,
                format: "RGBA8888",
                size: {
                    w: sourceImage.width,
                    h: sourceImage.height
                },
                scale: "1"
            }
        };
        
        // Convert frames and sort alphabetically
        const frameNames = Object.keys(texture.frames)
            .filter(frameName => frameName !== '__BASE')
            .sort(); // Sort alphabetically
        
        frameNames.forEach(frameName => {
            const frame = texture.frames[frameName];
            exportData.frames.push({
                filename: frameName,
                frame: {
                    x: Math.round(frame.cutX || frame.x || 0),
                    y: Math.round(frame.cutY || frame.y || 0),
                    w: Math.round(frame.cutWidth || frame.width || 0),
                    h: Math.round(frame.cutHeight || frame.height || 0)
                },
                rotated: false,
                trimmed: false,
                spriteSourceSize: {
                    x: 0,
                    y: 0,
                    w: Math.round(frame.cutWidth || frame.width || 0),
                    h: Math.round(frame.cutHeight || frame.height || 0)
                },
                sourceSize: {
                    w: Math.round(frame.cutWidth || frame.width || 0),
                    h: Math.round(frame.cutHeight || frame.height || 0)
                }
            });
        });
        
        // Auto-detect animation sequences
        const animations = this.detectAnimationSequences(frameNames);
        if (Object.keys(animations).length > 0) {
            exportData.meta.animations = animations;
        }
        
        // Create downloadable JSON
        const jsonString = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.currentAtlas.replace('Atlas', '')}-spritesheet.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        } catch (error) {
            logger.error('[SPRITE EDITOR] Export failed:', error);
            // eslint-disable-next-line no-alert
            alert('Export failed: ' + error.message);
        }
    }
    
    detectAnimationSequences(frameNames) {
        const animations = {};
        const sequenceMap = new Map();
        
        // Group frames by their base name (everything before the last dash and number)
        frameNames.forEach(frameName => {
            // Match patterns like "aphid-1", "darkling-beetle-6", "score-0", etc.
            const match = frameName.match(/^(.+)-(\d+)$/);
            if (match) {
                const baseName = match[1];
                const frameNumber = parseInt(match[2], 10);
                
                if (!sequenceMap.has(baseName)) {
                    sequenceMap.set(baseName, []);
                }
                sequenceMap.get(baseName).push({ name: frameName, number: frameNumber });
            }
        });
        
        // Convert valid sequences to animations (2+ frames)
        sequenceMap.forEach((frames, baseName) => {
            if (frames.length >= 2) {
                // Sort by frame number and extract names
                const sortedFrames = frames
                    .sort((a, b) => a.number - b.number)
                    .map(f => f.name);
                animations[baseName] = sortedFrames;
            }
        });
        
        // Special handling for common patterns
        const specialPatterns = [
            // Handle plant sequences (healthy + stages)
            {
                pattern: /^plant-(healthy|stage\d+)$/,
                groupName: 'plant',
                getFrames: () => {
                    const plantFrames = frameNames.filter(name => name.startsWith('plant-'));
                    if (plantFrames.length >= 2) {
                        // Sort with healthy first, then stages by number
                        return plantFrames.sort((a, b) => {
                            if (a === 'plant-healthy') return -1;
                            if (b === 'plant-healthy') return 1;
                            const aNum = parseInt(a.match(/stage(\d+)/)?.[1] || '0', 10);
                            const bNum = parseInt(b.match(/stage(\d+)/)?.[1] || '0', 10);
                            return aNum - bNum;
                        });
                    }
                    return null;
                }
            },
            // Handle nest sequences (numbered + full)
            {
                pattern: /^nest-(\d+|full)$/,
                groupName: 'nest',
                getFrames: () => {
                    const nestFrames = frameNames.filter(name => name.startsWith('nest-'));
                    if (nestFrames.length >= 2) {
                        return nestFrames.sort((a, b) => {
                            if (a === 'nest-full') return 1;
                            if (b === 'nest-full') return -1;
                            const aNum = parseInt(a.match(/nest-(\d+)/)?.[1] || '0', 10);
                            const bNum = parseInt(b.match(/nest-(\d+)/)?.[1] || '0', 10);
                            return aNum - bNum;
                        });
                    }
                    return null;
                }
            }
        ];
        
        specialPatterns.forEach(pattern => {
            const frames = pattern.getFrames();
            if (frames) {
                animations[pattern.groupName] = frames;
            }
        });
        return animations;
    }

    toggleLock() {
        if (!this.selectedFrame) {
            return;
        }
        
        const isLocked = this.lockedFrames.has(this.selectedFrame);
        const lockBtn = document.getElementById('lockButton');
        
        if (isLocked) {
            // Unlock the frame
            this.lockedFrames.delete(this.selectedFrame);
            lockBtn.textContent = '🔓 Unlock';
            lockBtn.style.background = '#28a745';
} else {
            // Lock the frame
            this.lockedFrames.add(this.selectedFrame);
            lockBtn.textContent = '🔒 Lock';
            lockBtn.style.background = '#dc3545';
}
        
        // Update frame list to show lock status
        this.updateFrameListSelection(this.selectedFrame);
        
        // Redraw canvas to show lock indicator
        this.redrawCanvasWithCurrentFrame(this.currentAtlas);
    }
    
    isFrameLocked(frameName) {
        return this.lockedFrames.has(frameName);
    }
    
    showHelpDialog() {
        helpUI.show();
    }
    
    deleteSelectedFrame() {
        if (!this.selectedFrame) return;

        // eslint-disable-next-line no-alert
        if (confirm(`Delete frame "${this.selectedFrame}"?`)) {
            // Save state before deletion
            this.saveStateToHistory('frame_delete', this.selectedFrame);

            const scene = game.scene.getScene('MinigameScene');
            const texture = scene.textures.get(this.currentAtlas);

            delete texture.frames[this.selectedFrame];

            this.selectedFrame = null;
            this.populateFrameList(texture, this.currentAtlas);
            this.redrawCanvasWithCurrentFrame(this.currentAtlas);
        }
    }
    
    showContextMenu(e, mouseX, mouseY) {
        e.preventDefault();

        try {
            // Check if we're on a frame
            const interaction = this.getFrameInteraction(mouseX, mouseY);
            // Remove any existing context menu
        const existingMenu = document.getElementById('sprite-context-menu');
        if (existingMenu) {
            existingMenu.remove();
        }
        
        // Create context menu
        const menu = document.createElement('div');
        menu.id = 'sprite-context-menu';
        menu.style.cssText = `
            position: fixed;
            left: ${e.clientX}px;
            top: ${e.clientY}px;
            background: rgba(0, 0, 0, 0.9);
            border: 1px solid #4CAF50;
            border-radius: 4px;
            padding: 5px 0;
            z-index: 1000002;
            font-size: 12px;
            min-width: 150px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
        `;
        
        const menuItems = [];
        
        if (interaction) {
            // Frame-specific menu
            menuItems.push(
                { text: `Select "${interaction.frameName}"`, action: () => {
                    this.selectedFrame = interaction.frameName;
                    this.updateFrameListSelection(interaction.frameName);
                    this.redrawCanvasWithCurrentFrame(this.currentAtlas);
                }},
                { text: `Duplicate "${interaction.frameName}"`, action: () => this.duplicateFrame(interaction.frameName), disabled: this.isFrameLocked(interaction.frameName) },
                { text: `Delete "${interaction.frameName}"`, action: () => {
                    this.selectedFrame = interaction.frameName;
                    this.deleteSelectedFrame();
                }, disabled: this.isFrameLocked(interaction.frameName) },
                { text: '---' },
                { text: `${this.isFrameLocked(interaction.frameName) ? 'Unlock' : 'Lock'} "${interaction.frameName}"`, action: () => {
                    this.selectedFrame = interaction.frameName;
                    this.toggleLock();
                }},
                { text: 'Create New Frame Here', action: () => this.startFrameCreationAt(mouseX, mouseY) }
            );
        } else {
            // Empty space menu
            menuItems.push(
                { text: 'Create New Frame', action: () => this.startFrameCreationAt(mouseX, mouseY) },
                { text: '---' },
                { text: 'Reset Pan & Zoom', action: () => {
                    this.panOffset = { x: 0, y: 0 };
                    this.currentZoomLevel = 1.0;
                    this.redrawCanvasWithCurrentFrame(this.currentAtlas);
                }}
            );
        }
        
        menuItems.push(
            { text: '---' },
            { text: 'Toggle Grid (G)', action: () => {
                this.gridVisible = !this.gridVisible;
                this.redrawCanvasWithCurrentFrame(this.currentAtlas);
            }},
            { text: 'Export JSON', action: () => this.exportAtlasJSON() },
            { text: 'Help (?)', action: () => this.showHelpDialog() }
        );
        
        menuItems.forEach(item => {
            if (item.text === '---') {
                const separator = document.createElement('div');
                separator.style.cssText = 'height: 1px; background: #333; margin: 2px 0;';
                menu.appendChild(separator);
            } else {
                const menuItem = document.createElement('div');
                menuItem.textContent = item.text;
                
                if (item.disabled) {
                    menuItem.style.cssText = `
                        padding: 5px 12px;
                        cursor: not-allowed;
                        color: #666;
                        transition: background 0.2s;
                    `;
                    menuItem.onmouseenter = () => menuItem.style.background = 'rgba(128, 128, 128, 0.2)';
                    menuItem.onmouseleave = () => menuItem.style.background = 'transparent';
                    menuItem.onclick = (e) => {
                        e.preventDefault();
};
                } else {
                    menuItem.style.cssText = `
                        padding: 5px 12px;
                        cursor: pointer;
                        color: white;
                        transition: background 0.2s;
                    `;
                    menuItem.onmouseenter = () => menuItem.style.background = 'rgba(76, 175, 80, 0.3)';
                    menuItem.onmouseleave = () => menuItem.style.background = 'transparent';
                    menuItem.onclick = () => {
                        item.action();
                        menu.remove();
                    };
                }
                menu.appendChild(menuItem);
            }
        });
        
        document.body.appendChild(menu);
            // Remove menu when clicking elsewhere
            setTimeout(() => {
                document.addEventListener('click', (e) => {
                    if (!menu.contains(e.target)) {
                        menu.remove();
                    }
                }, { once: true });
            }, 10);
        
        } catch (error) {
            logger.error('[DEBUG] Error in showContextMenu:', error);
        }
    }
    
    startFrameCreationAt(mouseX, mouseY) {
        this.dragState.isCreatingFrame = true;
        this.dragState.startX = mouseX;
        this.dragState.startY = mouseY;
        this.selectedFrame = null;
        const canvas = document.getElementById('spritesheetCanvas');
        canvas.style.cursor = 'crosshair';
    }
    
    duplicateFrame(frameName) {
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(this.currentAtlas);
        const originalFrame = texture.frames[frameName];

        if (!originalFrame) return;

        // eslint-disable-next-line no-alert
        const newName = prompt(`Duplicate frame "${frameName}" as:`, frameName + '_copy');
        if (newName && newName.trim()) {
            this.saveStateToHistory('frame_create', newName.trim());
            
            // Copy frame data
            const newFrame = {
                cutX: originalFrame.cutX || originalFrame.x || 0,
                cutY: originalFrame.cutY || originalFrame.y || 0,
                cutWidth: originalFrame.cutWidth || originalFrame.width || 0,
                cutHeight: originalFrame.cutHeight || originalFrame.height || 0,
                x: originalFrame.cutX || originalFrame.x || 0,
                y: originalFrame.cutY || originalFrame.y || 0,
                width: originalFrame.cutWidth || originalFrame.width || 0,
                height: originalFrame.cutHeight || originalFrame.height || 0,
                rotated: false,
                trimmed: false,
                spriteSourceSize: { 
                    x: 0, y: 0, 
                    w: originalFrame.cutWidth || originalFrame.width || 0, 
                    h: originalFrame.cutHeight || originalFrame.height || 0 
                },
                sourceSize: { 
                    w: originalFrame.cutWidth || originalFrame.width || 0, 
                    h: originalFrame.cutHeight || originalFrame.height || 0 
                }
            };
            
            texture.frames[newName.trim()] = newFrame;
            
            this.populateFrameList(texture, this.currentAtlas);
            this.selectedFrame = newName.trim();
            this.updateFrameListSelection(newName.trim());
            this.redrawCanvasWithCurrentFrame(this.currentAtlas);
}
    }
    
    saveStateToHistory(actionType, frameName, _frameData = null) {
        try {
            const scene = game.scene.getScene('MinigameScene');
            const texture = scene.textures.get(this.currentAtlas);
            
            if (!scene || !texture) {
                logger.error('[UNDO] Cannot save state: missing scene or texture');
                return;
            }
            
            // Clear any future history if we're not at the end
            if (this.historyIndex < this.actionHistory.length - 1) {
                this.actionHistory = this.actionHistory.slice(0, this.historyIndex + 1);
            }
            
            // Create state snapshot with clean frame data (no circular references)
            const cleanFrames = {};
            Object.keys(texture.frames).forEach(frameKey => {
                if (frameKey === '__BASE') return;
                
                const frame = texture.frames[frameKey];
                cleanFrames[frameKey] = {
                    cutX: frame.cutX || frame.x || 0,
                    cutY: frame.cutY || frame.y || 0,
                    cutWidth: frame.cutWidth || frame.width || 0,
                    cutHeight: frame.cutHeight || frame.height || 0,
                    x: frame.cutX || frame.x || 0,
                    y: frame.cutY || frame.y || 0,
                    width: frame.cutWidth || frame.width || 0,
                    height: frame.cutHeight || frame.height || 0,
                    rotated: false,
                    trimmed: false,
                    spriteSourceSize: { 
                        x: 0, y: 0, 
                        w: frame.cutWidth || frame.width || 0, 
                        h: frame.cutHeight || frame.height || 0 
                    },
                    sourceSize: { 
                        w: frame.cutWidth || frame.width || 0, 
                        h: frame.cutHeight || frame.height || 0 
                    }
                };
            });
            
            const state = {
                actionType,
                frameName,
                timestamp: Date.now(),
                frames: cleanFrames
            };
            
            this.actionHistory.push(state);
            
            // Limit history size
            if (this.actionHistory.length > this.maxHistorySize) {
                this.actionHistory.shift();
            } else {
                this.historyIndex++;
            }
} catch (error) {
            logger.error('[UNDO] Failed to save state:', error);
        }
    }
    
    undo() {
if (this.historyIndex < 0) {
return;
        }
        
        try {
            const state = this.actionHistory[this.historyIndex];
const scene = game.scene.getScene('MinigameScene');
            const texture = scene.textures.get(this.currentAtlas);
            
            if (!scene || !texture) {
                logger.error('[UNDO] Cannot undo: missing scene or texture');
                return;
            }
            
            // Restore the previous state
            texture.frames = JSON.parse(JSON.stringify(state.frames));
            this.historyIndex--;
            
            // Update UI
            this.populateFrameList(texture, this.currentAtlas);
            this.selectedFrame = null;
            this.redrawCanvasWithCurrentFrame(this.currentAtlas);
} catch (error) {
            logger.error('[UNDO] Failed to undo:', error);
        }
    }
    
    redo() {
        if (this.historyIndex >= this.actionHistory.length - 1) {
return;
        }
        
        this.historyIndex++;
        const state = this.actionHistory[this.historyIndex];
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(this.currentAtlas);
        
        // Apply the next state
        texture.frames = JSON.parse(JSON.stringify(state.frames));
        
        // Update UI
        this.populateFrameList(texture, this.currentAtlas);
        this.selectedFrame = null;
        this.redrawCanvasWithCurrentFrame(this.currentAtlas);
}
    
    drawFrameOutlines(ctx, texture) {
Object.keys(texture.frames).forEach(frameName => {
            if (frameName === '__BASE') return;
            
            const frame = texture.frames[frameName];
            const x = frame.cutX || frame.x || 0;
            const y = frame.cutY || frame.y || 0;
            const width = frame.cutWidth || frame.width || 0;
            const height = frame.cutHeight || frame.height || 0;
            
            ctx.strokeStyle = '#ff9800';
            ctx.lineWidth = 1;
            ctx.strokeRect(x, y, width, height);
            
            // Draw frame name
            ctx.fillStyle = '#ff9800';
            ctx.font = '10px Arial';
            ctx.fillText(frameName, x + 2, y - 2);
        });
    }
    
    drawFrameOutlinesScaled(ctx, texture, scale, offsetX, offsetY, highlightFrame = null) {
// Apply pan offset to drawing coordinates
        const panAdjustedX = offsetX + this.panOffset.x;
        const panAdjustedY = offsetY + this.panOffset.y;
        
        Object.keys(texture.frames).forEach(frameName => {
            if (frameName === '__BASE') return;
            
            const frame = texture.frames[frameName];
            const x = (frame.cutX || frame.x || 0) * scale + panAdjustedX;
            const y = (frame.cutY || frame.y || 0) * scale + panAdjustedY;
            const width = (frame.cutWidth || frame.width || 0) * scale;
            const height = (frame.cutHeight || frame.height || 0) * scale;
            
            // Choose color based on whether this frame is highlighted or selected
            const isSelected = frameName === this.selectedFrame;
            const isHighlighted = frameName === highlightFrame;
            const isLocked = this.isFrameLocked(frameName);
            
            if (isSelected) {
                // Selected frame: thin green border with minimal fill
                ctx.fillStyle = 'rgba(76, 175, 80, 0.1)';
                ctx.fillRect(x, y, width, height);
                ctx.strokeStyle = '#4CAF50';
                ctx.lineWidth = 1;
                ctx.strokeRect(x, y, width, height);
                
                // Add subtle dashed outer border
                ctx.setLineDash([4, 2]);
                ctx.strokeStyle = '#00ff00';
                ctx.lineWidth = 1;
                ctx.strokeRect(x - 1, y - 1, width + 2, height + 2);
                ctx.setLineDash([]);
            } else if (isHighlighted) {
                // Hovered frame: very light green
                ctx.fillStyle = 'rgba(76, 175, 80, 0.05)';
                ctx.fillRect(x, y, width, height);
                ctx.strokeStyle = '#4CAF50';
                ctx.lineWidth = 1;
                ctx.strokeRect(x, y, width, height);
            } else {
                // Normal frame: thin orange border
                ctx.strokeStyle = 'rgba(255, 152, 0, 0.6)';
                ctx.lineWidth = 1;
                ctx.strokeRect(x, y, width, height);
            }
            
            // Draw frame name with background for better visibility
            const textMetrics = ctx.measureText(frameName);
            const textWidth = textMetrics.width;
            const textHeight = 14;
            
            if (isSelected) {
                // Selected frame: white text on green background
                ctx.fillStyle = '#4CAF50';
                ctx.fillRect(x, y - textHeight - 4, textWidth + 8, textHeight + 4);
                ctx.fillStyle = 'white';
                ctx.font = 'bold 12px Arial';
                ctx.fillText(frameName, x + 4, y - 6);
            } else {
                // Other frames: standard orange text
                ctx.fillStyle = isHighlighted ? '#4CAF50' : '#ff9800';
                ctx.font = '12px Arial';
                ctx.fillText(frameName, x + 2, y - 2);
            }
            
            // Draw lock icon for locked frames
            if (isLocked) {
                ctx.fillStyle = '#dc3545';
                ctx.fillRect(x + width - 20, y + 2, 16, 16);
                ctx.fillStyle = 'white';
                ctx.font = '12px Arial';
                ctx.fillText('🔒', x + width - 18, y + 13);
            }
            
            // Draw resize handles for selected frame (but not if locked)
            if (isSelected && !isLocked) {
                this.drawResizeHandles(ctx, x, y, width, height);
            }
        });
    }
    
    drawResizeHandles(ctx, frameX, frameY, frameWidth, frameHeight) {
        const handleSize = 6;
        const halfHandle = handleSize / 2;
        
        // Handle positions
        const handles = [
            { x: frameX - halfHandle, y: frameY - halfHandle }, // top-left
            { x: frameX + frameWidth - halfHandle, y: frameY - halfHandle }, // top-right
            { x: frameX - halfHandle, y: frameY + frameHeight - halfHandle }, // bottom-left
            { x: frameX + frameWidth - halfHandle, y: frameY + frameHeight - halfHandle }, // bottom-right
            { x: frameX + frameWidth/2 - halfHandle, y: frameY - halfHandle }, // top-center
            { x: frameX + frameWidth/2 - halfHandle, y: frameY + frameHeight - halfHandle }, // bottom-center
            { x: frameX - halfHandle, y: frameY + frameHeight/2 - halfHandle }, // left-center
            { x: frameX + frameWidth - halfHandle, y: frameY + frameHeight/2 - halfHandle } // right-center
        ];
        
        // Draw handles
        ctx.fillStyle = 'rgba(76, 175, 80, 0.8)';
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.lineWidth = 1;
        
        handles.forEach(handle => {
            ctx.fillRect(handle.x, handle.y, handleSize, handleSize);
            ctx.strokeRect(handle.x, handle.y, handleSize, handleSize);
        });
    }
    
    updateFrameFromInput() {
        const frameNameInput = document.getElementById('frameNameInput');
        const frameName = frameNameInput.value.trim();
        if (frameName && this.currentSelection) {
            this.updateFrameData(this.currentAtlas, frameName, this.currentSelection);
            frameNameInput.value = '';
            document.getElementById('coordDisplay').textContent = `Updated frame: ${frameName}`;
        }
    }
    
    closeFullScreenEditor() {
// Remove the full-screen editor
        const editor = document.getElementById('fullScreenSpriteEditor');
        if (editor) {
            editor.remove();
}
        
        // Show the game container again
        const gameContainer = document.getElementById('gameContainer');
        if (gameContainer) {
            gameContainer.style.display = 'block';
}
        
        // Re-enable game input
        this.enableGameInput();
}
    
    moveSelectedFrame(direction) {
        if (!this.selectedFrame) return;
        if (this.isFrameLocked(this.selectedFrame)) {
return;
        }
// Save state for undo
        this.saveStateToHistory('frame_modify', this.selectedFrame);
        
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(this.currentAtlas);
        const frame = texture.frames[this.selectedFrame];
        
        if (!frame) return;
        
        // Get current position
        let newX = frame.cutX || frame.x || 0;
        let newY = frame.cutY || frame.y || 0;
        
        // Calculate movement (1 pixel per keypress)
        switch (direction) {
            case 'ArrowUp':
                newY = Math.max(0, newY - 1);
                break;
            case 'ArrowDown':
                newY = newY + 1;
                break;
            case 'ArrowLeft':
                newX = Math.max(0, newX - 1);
                break;
            case 'ArrowRight':
                newX = newX + 1;
                break;
        }
        
        // Update frame position
        frame.cutX = newX;
        frame.cutY = newY;
        frame.x = newX;
        frame.y = newY;
        
        // Update coordinate display with input controls
        const width = frame.cutWidth || frame.width || 0;
        const height = frame.cutHeight || frame.height || 0;
        
        const coordDisplay = document.getElementById('coordDisplay');
        if (coordDisplay) {
            coordDisplay.innerHTML = `
                <div style="background: #4CAF50; color: white; padding: 8px; border-radius: 4px; margin-bottom: 8px; font-weight: bold; text-align: center; font-size: 14px;">
                     SELECTED: "${this.selectedFrame}"
                </div>
                <div style="background: rgba(255,255,255,0.1); padding: 8px; border-radius: 4px; font-family: monospace;">
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
                        <div>
                            <label style="display: block; font-size: 10px; color: #4CAF50; margin-bottom: 2px;">X Position:</label>
                            <input type="number" id="frameX" value="${newX}" style="width: 100%; background: rgba(0,0,0,0.8); border: 1px solid #4CAF50; color: white; padding: 4px; font-size: 12px; font-family: monospace;">
                        </div>
                        <div>
                            <label style="display: block; font-size: 10px; color: #4CAF50; margin-bottom: 2px;">Y Position:</label>
                            <input type="number" id="frameY" value="${newY}" style="width: 100%; background: rgba(0,0,0,0.8); border: 1px solid #4CAF50; color: white; padding: 4px; font-size: 12px; font-family: monospace;">
                        </div>
                    </div>
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
                        <div>
                            <label style="display: block; font-size: 10px; color: #4CAF50; margin-bottom: 2px;">Width:</label>
                            <input type="number" id="frameW" value="${width}" style="width: 100%; background: rgba(0,0,0,0.8); border: 1px solid #4CAF50; color: white; padding: 4px; font-size: 12px; font-family: monospace;">
                        </div>
                        <div>
                            <label style="display: block; font-size: 10px; color: #4CAF50; margin-bottom: 2px;">Height:</label>
                            <input type="number" id="frameH" value="${height}" style="width: 100%; background: rgba(0,0,0,0.8); border: 1px solid #4CAF50; color: white; padding: 4px; font-size: 12px; font-family: monospace;">
                        </div>
                    </div>
                    <div style="display: flex; gap: 8px;">
                        <button id="applyFrameValues" style="flex: 1; background: #4CAF50; color: white; border: none; padding: 6px; border-radius: 4px; font-size: 11px; cursor: pointer;">Apply Changes</button>
                        <button id="resetFrameValues" style="background: #ff9800; color: white; border: none; padding: 6px 12px; border-radius: 4px; font-size: 11px; cursor: pointer;">Reset</button>
                    </div>
                </div>
            `;
            
            // Add event handlers for the input controls
            this.setupFrameInputHandlers(this.selectedFrame);
        }
        
        // Update frame list selection and redraw
        this.updateFrameListSelection(this.selectedFrame);
        this.redrawCanvasWithCurrentFrame(this.currentAtlas);
}
    
    setupFrameInputHandlers(frameName) {
        // Set up Apply Changes button
        const applyBtn = document.getElementById('applyFrameValues');
        if (applyBtn) {
            applyBtn.onclick = () => {
                this.applyFrameInputValues(frameName);
            };
        }
        
        // Set up Reset button  
        const resetBtn = document.getElementById('resetFrameValues');
        if (resetBtn) {
            resetBtn.onclick = () => {
                this.resetFrameInputValues(frameName);
            };
        }
        
        // Set up Enter key handling on input fields
        ['frameX', 'frameY', 'frameW', 'frameH'].forEach(inputId => {
            const input = document.getElementById(inputId);
            if (input) {
                input.onkeydown = (e) => {
                    if (e.key === 'Enter') {
                        this.applyFrameInputValues(frameName);
                    }
                };
                
                // Also allow real-time updates on input change
                input.oninput = () => {
                    // Optional: Could add real-time preview here
                };
            }
        });
    }
    
    applyFrameInputValues(frameName) {
        const frameX = document.getElementById('frameX');
        const frameY = document.getElementById('frameY'); 
        const frameW = document.getElementById('frameW');
        const frameH = document.getElementById('frameH');
        
        if (!frameX || !frameY || !frameW || !frameH) return;
        
        // Get values from inputs
        const newX = parseInt(frameX.value) || 0;
        const newY = parseInt(frameY.value) || 0;
        const newWidth = parseInt(frameW.value) || 1;
        const newHeight = parseInt(frameH.value) || 1;
// Save state for undo
        this.saveStateToHistory('frame_modify', frameName);
        
        // Get frame and update values
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(this.currentAtlas);
        const frame = texture.frames[frameName];
        
        if (frame) {
            // Ensure minimum size
            const finalWidth = Math.max(1, newWidth);
            const finalHeight = Math.max(1, newHeight);
            const finalX = Math.max(0, newX);
            const finalY = Math.max(0, newY);
            
            // Update frame data
            frame.cutX = finalX;
            frame.cutY = finalY;
            frame.cutWidth = finalWidth;
            frame.cutHeight = finalHeight;
            frame.x = finalX;
            frame.y = finalY;
            frame.width = finalWidth;
            frame.height = finalHeight;
            
            // Update sprite source size
            if (frame.spriteSourceSize) {
                frame.spriteSourceSize.w = finalWidth;
                frame.spriteSourceSize.h = finalHeight;
            }
            if (frame.sourceSize) {
                frame.sourceSize.w = finalWidth;
                frame.sourceSize.h = finalHeight;
            }
// Update displays and redraw
            this.updateFrameListSelection(frameName);
            this.redrawCanvasWithCurrentFrame(this.currentAtlas);
            
            // Show success feedback
            const applyBtn = document.getElementById('applyFrameValues');
            if (applyBtn) {
                const originalText = applyBtn.textContent;
                applyBtn.textContent = '✓ Applied!';
                applyBtn.style.background = '#4CAF50';
                setTimeout(() => {
                    applyBtn.textContent = originalText;
                    applyBtn.style.background = '#4CAF50';
                }, 1000);
            }
        }
    }
    
    resetFrameInputValues(frameName) {
        // Get original frame values and reset inputs
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(this.currentAtlas);
        const frame = texture.frames[frameName];
        
        if (frame) {
            const frameX = document.getElementById('frameX');
            const frameY = document.getElementById('frameY');
            const frameW = document.getElementById('frameW');
            const frameH = document.getElementById('frameH');
            
            if (frameX) frameX.value = frame.cutX || frame.x || 0;
            if (frameY) frameY.value = frame.cutY || frame.y || 0;
            if (frameW) frameW.value = frame.cutWidth || frame.width || 0;
            if (frameH) frameH.value = frame.cutHeight || frame.height || 0;
}
    }
    
    drawExistingFrames(ctx, texture, scale, highlightFrame = null) {
        Object.keys(texture.frames).forEach(frameName => {
            if (frameName === '__BASE') return;
            
            const frame = texture.frames[frameName];
            
            // Get frame coordinates from the loaded texture
            // Phaser frame objects use different property names
            const x = frame.cutX || frame.x || 0;
            const y = frame.cutY || frame.y || 0;
            const width = frame.cutWidth || frame.width || 0;
            const height = frame.cutHeight || frame.height || 0;
            
            // Debug first few frames
            if (frameName === 'base' || frameName === 'glass-overlay' || frameName.includes('aphid')) {
                // Debug check for specific frames - no action needed
            }
            
            // Choose color based on whether this frame is highlighted
            if (frameName === highlightFrame) {
                ctx.strokeStyle = '#4CAF50';
                ctx.lineWidth = 3;
                ctx.fillStyle = 'rgba(76, 175, 80, 0.2)';
                ctx.fillRect(
                    x * scale,
                    y * scale,
                    width * scale,
                    height * scale
                );
            } else {
                ctx.strokeStyle = '#ff9800';
                ctx.lineWidth = 1;
            }
            
            ctx.strokeRect(
                x * scale,
                y * scale,
                width * scale,
                height * scale
            );
            
            // Draw frame name
            ctx.fillStyle = frameName === highlightFrame ? '#4CAF50' : '#ff9800';
            ctx.font = '10px Courier New';
            ctx.fillText(frameName, x * scale + 2, y * scale - 2);
        });
    }
    
    populateFrameList(texture, atlasKey) {
        // Get the frameList that's inside our full-screen editor (not the old modal one)
        const fullScreenEditor = document.getElementById('fullScreenSpriteEditor');
        const frameList = fullScreenEditor ? fullScreenEditor.querySelector('#frameList') : null;
        
        if (!frameList) {
            logger.error('[SPRITE EDITOR] Frame list element not found inside full-screen editor!');
            return;
        }
frameList.innerHTML = ''; // Clear existing list
        
        
        // Debug: Log all available frames
const frameNames = Object.keys(texture.frames).filter(name => name !== '__BASE' && name !== '__default');
        frameNames.sort();
if (frameNames.length === 0) {
            frameList.innerHTML = '<div style="color: #999; padding: 10px;">No frames found. Check console for debugging info.</div>';
            logger.error('[SPRITE EDITOR] No frames found after filtering!');
return;
        }
frameNames.forEach(frameName => {
            const frame = texture.frames[frameName];
            const x = frame.cutX || frame.x || 0;
            const y = frame.cutY || frame.y || 0;
            const width = frame.cutWidth || frame.width || 0;
            const height = frame.cutHeight || frame.height || 0;
            
            const frameItem = document.createElement('div');
            frameItem.className = 'frame-item';
            
            // Set styles individually to ensure they work
            frameItem.style.background = 'rgba(255, 255, 255, 0.1)';
            frameItem.style.border = '1px solid rgba(255, 255, 255, 0.2)';
            frameItem.style.borderRadius = '6px';
            frameItem.style.padding = '8px';
            frameItem.style.cursor = 'pointer';
            frameItem.style.transition = 'all 0.2s ease';
            frameItem.style.fontSize = '12px';
            frameItem.style.color = '#ccc';
            frameItem.style.marginBottom = '8px';
            frameItem.style.display = 'block';
            frameItem.style.width = '100%';
            frameItem.style.minHeight = '60px';  // Ensure it has height
            
            // Check if frame is locked and add lock indicator
            const isLocked = this.isFrameLocked(frameName);
            const lockIcon = isLocked ? '🔒 ' : '';
            
            frameItem.innerHTML = `
                <div style="font-weight: bold; margin-bottom: 2px;">${lockIcon}${frameName}</div>
                <div style="font-size: 10px; color: #aaa;">x:${x} y:${y}</div>
                <div style="font-size: 10px; color: #aaa;">w:${width} h:${height}</div>
            `;
            frameItem.dataset.frameName = frameName;
            
            // Add special styling for locked frames
            if (isLocked) {
                frameItem.style.borderColor = '#dc3545';
                frameItem.style.background = 'rgba(220, 53, 69, 0.1)';
            }
            
            // Add hover and active styles
            frameItem.addEventListener('mouseenter', () => {
                frameItem.style.background = 'rgba(76, 175, 80, 0.2)';
                frameItem.style.borderColor = '#4CAF50';
                frameItem.style.color = 'white';
            });
            
            frameItem.addEventListener('mouseleave', () => {
                if (!frameItem.classList.contains('active')) {
                    frameItem.style.background = 'rgba(255, 255, 255, 0.1)';
                    frameItem.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                    frameItem.style.color = '#ccc';
                }
            });
frameItem.addEventListener('click', () => {
                // Remove active class from all items and reset their styles
                frameList.querySelectorAll('.frame-item').forEach(item => {
                    item.classList.remove('active');
                    item.style.background = 'rgba(255, 255, 255, 0.1)';
                    item.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                    item.style.color = '#ccc';
                });
                
                // Add active class to clicked item and set active styles
                frameItem.classList.add('active');
                frameItem.style.background = 'rgba(76, 175, 80, 0.4)';
                frameItem.style.borderColor = '#4CAF50';
                frameItem.style.color = 'white';
                
                // Redraw canvas with highlighted frame
                this.redrawWithHighlight(atlasKey, frameName);
                
                // Update coord display with frame info
                const frame = texture.frames[frameName];
                
                // Debug log to see complete frame structure first
// Phaser frame objects have different property structure
                const x = frame.cutX || frame.x || 0;
                const y = frame.cutY || frame.y || 0;
                const width = frame.cutWidth || frame.width || 0;
                const height = frame.cutHeight || frame.height || 0;
                
                const coordDisplay = document.getElementById('coordDisplay');
                coordDisplay.innerHTML = `
                    <div style="background: #4CAF50; color: white; padding: 8px; border-radius: 4px; margin-bottom: 8px; font-weight: bold; text-align: center; font-size: 14px;">
                         SELECTED: "${frameName}"
                    </div>
                    <div style="background: rgba(255,255,255,0.1); padding: 8px; border-radius: 4px; font-family: monospace;">
                        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
                            <div>
                                <label style="display: block; font-size: 10px; color: #4CAF50; margin-bottom: 2px;">X Position:</label>
                                <input type="number" id="frameX" value="${x}" style="width: 100%; background: rgba(0,0,0,0.8); border: 1px solid #4CAF50; color: white; padding: 4px; font-size: 12px; font-family: monospace;">
                            </div>
                            <div>
                                <label style="display: block; font-size: 10px; color: #4CAF50; margin-bottom: 2px;">Y Position:</label>
                                <input type="number" id="frameY" value="${y}" style="width: 100%; background: rgba(0,0,0,0.8); border: 1px solid #4CAF50; color: white; padding: 4px; font-size: 12px; font-family: monospace;">
                            </div>
                        </div>
                        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
                            <div>
                                <label style="display: block; font-size: 10px; color: #4CAF50; margin-bottom: 2px;">Width:</label>
                                <input type="number" id="frameW" value="${width}" style="width: 100%; background: rgba(0,0,0,0.8); border: 1px solid #4CAF50; color: white; padding: 4px; font-size: 12px; font-family: monospace;">
                            </div>
                            <div>
                                <label style="display: block; font-size: 10px; color: #4CAF50; margin-bottom: 2px;">Height:</label>
                                <input type="number" id="frameH" value="${height}" style="width: 100%; background: rgba(0,0,0,0.8); border: 1px solid #4CAF50; color: white; padding: 4px; font-size: 12px; font-family: monospace;">
                            </div>
                        </div>
                        <div style="display: flex; gap: 8px;">
                            <button id="applyFrameValues" style="flex: 1; background: #4CAF50; color: white; border: none; padding: 6px; border-radius: 4px; font-size: 11px; cursor: pointer;">Apply Changes</button>
                            <button id="resetFrameValues" style="background: #ff9800; color: white; border: none; padding: 6px 12px; border-radius: 4px; font-size: 11px; cursor: pointer;">Reset</button>
                        </div>
                    </div>
                `;
                
                // Also set the selectedFrame property for the editor
                this.selectedFrame = frameName;
                
                // Add event handlers for the input controls
                this.setupFrameInputHandlers(frameName);
                
                // Debug log to see what we actually extracted
// Pre-fill frame name input and current coordinates for editing
                document.getElementById('frameNameInput').value = frameName;
                
                // Set current selection to this frame's coordinates for editing
                this.currentSelection = { x: x, y: y, w: width, h: height };
            });
            
            frameList.appendChild(frameItem);
});
}
    
    updateFrameListSelection(frameName) {
        const fullScreenEditor = document.getElementById('fullScreenSpriteEditor');
        const frameList = fullScreenEditor ? fullScreenEditor.querySelector('#frameList') : null;
        
        if (!frameList) return;
        
        // Remove active class from all items and reset their styles
        frameList.querySelectorAll('.frame-item').forEach(item => {
            item.classList.remove('active');
            item.style.background = 'rgba(255, 255, 255, 0.1)';
            item.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            item.style.color = '#ccc';
        });
        
        // Find and activate the selected frame item
        const selectedItem = frameList.querySelector(`[data-frame-name="${frameName}"]`);
        if (selectedItem) {
            selectedItem.classList.add('active');
            selectedItem.style.background = 'rgba(76, 175, 80, 0.4)';
            selectedItem.style.borderColor = '#4CAF50';
            selectedItem.style.color = 'white';
            
            // Scroll into view if needed
            selectedItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        
        // Update lock button based on selected frame's lock state
        const lockBtn = document.getElementById('lockButton');
        if (lockBtn) {
            if (frameName) {
                const isLocked = this.isFrameLocked(frameName);
                if (isLocked) {
                    lockBtn.textContent = '🔒 Unlock';
                    lockBtn.style.background = '#dc3545';
                } else {
                    lockBtn.textContent = '🔓 Lock';
                    lockBtn.style.background = '#28a745';
                }
                lockBtn.disabled = false;
                lockBtn.style.opacity = '1';
            } else {
                // No frame selected
                lockBtn.textContent = '🔒 Lock';
                lockBtn.style.background = '#666';
                lockBtn.disabled = true;
                lockBtn.style.opacity = '0.5';
            }
        }
        
        // Update coordinate display
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(this.currentAtlas);
        const frame = texture.frames[frameName];
        
        if (frame) {
            const x = frame.cutX || frame.x || 0;
            const y = frame.cutY || frame.y || 0;
            const width = frame.cutWidth || frame.width || 0;
            const height = frame.cutHeight || frame.height || 0;
            
            const coordDisplay = document.getElementById('coordDisplay');
            coordDisplay.innerHTML = `
                <div style="background: #4CAF50; color: white; padding: 8px; border-radius: 4px; margin-bottom: 8px; font-weight: bold; text-align: center; font-size: 14px;">
                     SELECTED: "${frameName}"
                </div>
                <div style="background: rgba(255,255,255,0.1); padding: 8px; border-radius: 4px; font-family: monospace;">
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
                        <div>
                            <label style="display: block; font-size: 10px; color: #4CAF50; margin-bottom: 2px;">X Position:</label>
                            <input type="number" id="frameX" value="${x}" style="width: 100%; background: rgba(0,0,0,0.8); border: 1px solid #4CAF50; color: white; padding: 4px; font-size: 12px; font-family: monospace;">
                        </div>
                        <div>
                            <label style="display: block; font-size: 10px; color: #4CAF50; margin-bottom: 2px;">Y Position:</label>
                            <input type="number" id="frameY" value="${y}" style="width: 100%; background: rgba(0,0,0,0.8); border: 1px solid #4CAF50; color: white; padding: 4px; font-size: 12px; font-family: monospace;">
                        </div>
                    </div>
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
                        <div>
                            <label style="display: block; font-size: 10px; color: #4CAF50; margin-bottom: 2px;">Width:</label>
                            <input type="number" id="frameW" value="${width}" style="width: 100%; background: rgba(0,0,0,0.8); border: 1px solid #4CAF50; color: white; padding: 4px; font-size: 12px; font-family: monospace;">
                        </div>
                        <div>
                            <label style="display: block; font-size: 10px; color: #4CAF50; margin-bottom: 2px;">Height:</label>
                            <input type="number" id="frameH" value="${height}" style="width: 100%; background: rgba(0,0,0,0.8); border: 1px solid #4CAF50; color: white; padding: 4px; font-size: 12px; font-family: monospace;">
                        </div>
                    </div>
                    <div style="display: flex; gap: 8px;">
                        <button id="applyFrameValues" style="flex: 1; background: #4CAF50; color: white; border: none; padding: 6px; border-radius: 4px; font-size: 11px; cursor: pointer;">Apply Changes</button>
                        <button id="resetFrameValues" style="background: #ff9800; color: white; border: none; padding: 6px 12px; border-radius: 4px; font-size: 11px; cursor: pointer;">Reset</button>
                    </div>
                </div>
            `;
            
            // Pre-fill frame name input
            document.getElementById('frameNameInput').value = frameName;
            
            // Set current selection
            this.currentSelection = { x: x, y: y, w: width, h: height };
            this.selectedFrame = frameName;
            
            // Add event handlers for the input controls
            this.setupFrameInputHandlers(frameName);
        }
// Force visibility
        frameList.style.display = 'block';
    }
    
    redrawWithHighlight(atlasKey, highlightFrame) {
        // Use the existing redrawCanvasWithCurrentFrame method but with highlight
        this.redrawCanvasWithCurrentFrame(atlasKey, highlightFrame);
    }
    
    applyZoomToCanvas(zoomLevel) {
        const canvas = document.getElementById('spritesheetCanvas');
        if (!canvas) return;
// Store current zoom level
        this.currentZoomLevel = zoomLevel;
        
        // Get the current atlas and redraw at new zoom
        const atlasKey = this.currentAtlas;
        if (atlasKey) {
            this.redrawCanvasWithCurrentFrame(atlasKey);
        }
    }
    
    redrawCanvasWithCurrentFrame(atlasKey, forcedHighlightFrame = null) {
        const activeFrame = document.querySelector('.frame-item.active');
        const highlightFrame = forcedHighlightFrame || (activeFrame ? activeFrame.dataset.frameName : null);
        
        // Apply zoom multiplier to the base scale
        const canvas = document.getElementById('spritesheetCanvas');
        const ctx = canvas.getContext('2d');
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(atlasKey);
        const sourceImage = texture.source[0].image;
        
        // Calculate scale with zoom applied
        const baseScaleX = canvas.width / sourceImage.width;
        const baseScaleY = canvas.height / sourceImage.height;
        const baseScale = Math.min(baseScaleX, baseScaleY) * 0.9;
        const scale = baseScale * (this.currentZoomLevel || 1.0);
        
        const scaledWidth = sourceImage.width * scale;
        const scaledHeight = sourceImage.height * scale;
        const x = (canvas.width - scaledWidth) / 2;
        const y = (canvas.height - scaledHeight) / 2;
        
        // Apply panning offset to image position
        const panAdjustedX = x + this.panOffset.x;
        const panAdjustedY = y + this.panOffset.y;
        
        // Clear and draw background
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#333';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Draw the spritesheet image scaled and centered with panning
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(sourceImage, panAdjustedX, panAdjustedY, scaledWidth, scaledHeight);
        
        // Draw frames with highlight using scaled coordinates
        this.drawFrameOutlinesScaled(ctx, texture, scale, x, y, highlightFrame);
        
        // Draw grid if enabled
        if (this.gridVisible) {
            this.drawGrid(ctx, panAdjustedX, panAdjustedY, scaledWidth, scaledHeight, scale);
        }
    }
    
    drawGrid(ctx, offsetX, offsetY, width, height, scale) {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 1;
        
        const gridSize = 16 * scale; // 16px grid at base scale
        
        // Vertical lines
        for (let x = 0; x <= width; x += gridSize) {
            ctx.beginPath();
            ctx.moveTo(offsetX + x, offsetY);
            ctx.lineTo(offsetX + x, offsetY + height);
            ctx.stroke();
        }
        
        // Horizontal lines
        for (let y = 0; y <= height; y += gridSize) {
            ctx.beginPath();
            ctx.moveTo(offsetX, offsetY + y);
            ctx.lineTo(offsetX + width, offsetY + y);
            ctx.stroke();
        }
    }
    
    setupProperZoomControls(canvas, ctx, atlasKey) {
        // Create zoom controls and preview window
        const coordDisplay = document.getElementById('coordDisplay');
        
        if (!coordDisplay) {
            logger.error('[SPRITE EDITOR] Coord display element not found!');
            return;
        }
const zoomContainer = document.createElement('div');
        zoomContainer.innerHTML = `
            <div style="margin-top: 15px;">
                <div style="display: flex; gap: 10px; align-items: center; margin-bottom: 10px;">
                    <label style="color: #ccc; font-size: 12px;">Zoom:</label>
                    <select id="zoomSelect" style="background: rgba(255,255,255,0.1); border: 1px solid #ccc; color: white; padding: 4px;">
                        <option value="0.5">50%</option>
                        <option value="1" selected>100%</option>
                        <option value="2">200%</option>
                        <option value="4">400%</option>
                        <option value="8">800%</option>
                    </select>
                    <button id="fitToView" style="background: rgba(255,255,255,0.1); border: 1px solid #ccc; color: white; padding: 4px 8px; cursor: pointer;">Fit to View</button>
                </div>
                <div id="previewWindow" style="
                    width: 200px; 
                    height: 150px; 
                    border: 2px solid #4CAF50; 
                    background: rgba(0,0,0,0.5); 
                    position: relative;
                    overflow: hidden;
                    display: none;
                ">
                    <canvas id="previewCanvas" style="position: absolute; top: 0; left: 0;"></canvas>
                    <div id="viewportIndicator" style="
                        position: absolute;
                        border: 2px solid #ff9800;
                        background: rgba(255, 152, 0, 0.1);
                        pointer-events: none;
                    "></div>
                </div>
            </div>
        `;
        coordDisplay.parentNode.insertBefore(zoomContainer, coordDisplay.nextSibling);
        
        this.zoomLevel = 1;
        this.currentAtlas = atlasKey;
        
        // Make canvas container scrollable
        const canvasContainer = canvas.parentElement;
        canvasContainer.style.overflow = 'auto';
        canvasContainer.style.maxWidth = '800px';
        canvasContainer.style.maxHeight = '600px';
        canvasContainer.style.border = '2px solid #4CAF50';
        canvasContainer.style.backgroundColor = '#1a1a1a';
        
        // Zoom select handler
        document.getElementById('zoomSelect').onchange = (e) => {
            this.zoomLevel = parseFloat(e.target.value);
            this.updateCanvasView(canvas);
        };
        
        // Fit to view handler
        document.getElementById('fitToView').onclick = () => {
            this.zoomLevel = 1;
            document.getElementById('zoomSelect').value = '1';
            this.updateCanvasView(canvas);
            // Reset scroll position
            canvasContainer.scrollTop = 0;
            canvasContainer.scrollLeft = 0;
        };
        
        // Initial canvas setup
        this.updateCanvasView(canvas);
    }
    
    updateCanvasView(canvas) {
        // Instead of CSS transforms, actually resize the canvas and use a scrollable container
        const newWidth = this.sourceImageWidth * this.baseScale * this.zoomLevel;
        const newHeight = this.sourceImageHeight * this.baseScale * this.zoomLevel;
        
        // Resize canvas to new dimensions
        canvas.width = newWidth;
        canvas.height = newHeight;
        
        // Redraw the image at new size
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = false;
        
        // Calculate proper scale and position for centered display
        const scaleX = newWidth / this.sourceImage.width;
        const scaleY = newHeight / this.sourceImage.height;
        const scale = Math.min(scaleX, scaleY) * 0.9;
        const scaledWidth = this.sourceImage.width * scale;
        const scaledHeight = this.sourceImage.height * scale;
        const x = (newWidth - scaledWidth) / 2;
        const y = (newHeight - scaledHeight) / 2;
        
        ctx.drawImage(this.sourceImage, x, y, scaledWidth, scaledHeight);
        
        // Redraw frames and grid at new scale
        const scene = game.scene.getScene('MinigameScene');
        if (scene) {
            const texture = scene.textures.get(this.currentAtlas);
            this.drawExistingFrames(ctx, texture, this.baseScale * this.zoomLevel);
            if (this.showGrid) {
                this.drawGrid(ctx, newWidth, newHeight, 50 * this.zoomLevel);
            }
        }
}
    
    
    setupDragSelection(canvas, ctx, scale, coordDisplay, frameNameInput, atlasKey) {
let isDrawing = false;
        let startX, startY, currentX, currentY;
        
        const redrawCanvas = () => {
            const scene = game.scene.getScene('MinigameScene');
            const texture = scene.textures.get(this.currentAtlas);
            
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.imageSmoothingEnabled = false;
            
            // Calculate proper scale and position for centered display
            const scaleX = canvas.width / this.sourceImage.width;
            const scaleY = canvas.height / this.sourceImage.height;
            const scale = Math.min(scaleX, scaleY) * 0.9;
            const scaledWidth = this.sourceImage.width * scale;
            const scaledHeight = this.sourceImage.height * scale;
            const x = (canvas.width - scaledWidth) / 2;
            const y = (canvas.height - scaledHeight) / 2;
            
            ctx.drawImage(this.sourceImage, x, y, scaledWidth, scaledHeight);
            
            // Get currently active frame from frame list
            const activeFrame = document.querySelector('.frame-item.active');
            const highlightFrame = activeFrame ? activeFrame.dataset.frameName : null;
            
            const currentScale = this.baseScale * this.zoomLevel;
            this.drawExistingFrames(ctx, texture, currentScale, highlightFrame);
            this.drawGrid(ctx, canvas.width, canvas.height, 50 * this.zoomLevel);
        };
        
        // Prevent default context menu
        canvas.oncontextmenu = (e) => {
e.preventDefault();
            return false;
        };
        
        canvas.onmousedown = (e) => {
const rect = canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            
            // Handle right-click for context menu
            if (e.button === 2) {
this.showContextMenu(e, mouseX, mouseY);
                return;
            }
            
            // Handle left-click for drag selection
            if (e.button === 0) {
startX = mouseX;
                startY = mouseY;
                isDrawing = true;
            }
        };
        
        canvas.onmousemove = (e) => {
            const rect = canvas.getBoundingClientRect();
            currentX = e.clientX - rect.left;
            currentY = e.clientY - rect.top;
            
            if (isDrawing) {
                redrawCanvas();
                
                // Draw selection rectangle
                ctx.strokeStyle = '#4CAF50';
                ctx.lineWidth = 2;
                ctx.setLineDash([5, 5]);
                ctx.strokeRect(startX, startY, currentX - startX, currentY - startY);
                ctx.setLineDash([]);
                
                // Update coordinate display (convert canvas coordinates back to original image coordinates)
                const realX = Math.round(startX / (this.baseScale * this.zoomLevel));
                const realY = Math.round(startY / (this.baseScale * this.zoomLevel));
                const realW = Math.round((currentX - startX) / (this.baseScale * this.zoomLevel));
                const realH = Math.round((currentY - startY) / (this.baseScale * this.zoomLevel));
                
                coordDisplay.textContent = `x: ${realX}, y: ${realY}, w: ${realW}, h: ${realH}`;
            } else {
                // Show hover coordinates (convert canvas coordinates back to original image coordinates)
                const realX = Math.round(currentX / (this.baseScale * this.zoomLevel));
                const realY = Math.round(currentY / (this.baseScale * this.zoomLevel));
                coordDisplay.textContent = `Hover: x: ${realX}, y: ${realY}`;
            }
        };
        
        canvas.onmouseup = () => {
            if (isDrawing) {
                isDrawing = false;
                
                const realX = Math.round(startX / (this.baseScale * this.zoomLevel));
                const realY = Math.round(startY / (this.baseScale * this.zoomLevel));
                const realW = Math.round((currentX - startX) / (this.baseScale * this.zoomLevel));
                const realH = Math.round((currentY - startY) / (this.baseScale * this.zoomLevel));
                
                // Store selection data
                this.currentSelection = { x: realX, y: realY, w: realW, h: realH };
                
                coordDisplay.textContent = `Selected: x: ${realX}, y: ${realY}, w: ${realW}, h: ${realH}`;
                frameNameInput.focus();
            }
        };
        
        // Prevent input field from triggering game controls
        frameNameInput.onkeydown = (e) => {
            e.stopPropagation(); // Prevent game key handlers from firing
        };
        
        frameNameInput.onkeyup = (e) => {
            e.stopPropagation(); // Prevent game key handlers from firing
            
            // Allow Enter key to update frame
            if (e.key === 'Enter') {
                const frameName = frameNameInput.value.trim();
                if (frameName && this.currentSelection) {
                    this.updateFrameData(atlasKey, frameName, this.currentSelection);
                    frameNameInput.value = '';
                    coordDisplay.textContent = `Updated frame: ${frameName}`;
                }
            }
        };
        
        frameNameInput.onfocus = () => {
            // Prevent focus from triggering any game events
        };

        // Update frame button
        document.getElementById('updateFrame').onclick = () => {
            const frameName = frameNameInput.value.trim();
            if (frameName && this.currentSelection) {
                this.updateFrameData(atlasKey, frameName, this.currentSelection);
                frameNameInput.value = '';
                coordDisplay.textContent = `Updated frame: ${frameName}`;
            }
        };
        
        // Export JSON button (handled in createFullScreenSpriteEditor)
        // document.getElementById('exportJSON').onclick = () => {
        //     this.exportAtlasJSON();
        // };
    }
    
    updateFrameData(atlasKey, frameName, selection) {
const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(atlasKey);
        
        if (!texture) {
            logger.error('[SPRITE EDITOR] Texture not found:', atlasKey);
            return;
        }
        
        // Check if we're renaming an existing frame
        if (this.selectedFrame && this.selectedFrame !== frameName) {
            // We're renaming the frame
            const oldFrameData = texture.frames[this.selectedFrame];
            if (oldFrameData) {
                // Create new frame with updated name and data
                texture.frames[frameName] = {
                    cutX: selection.x,
                    cutY: selection.y,
                    cutWidth: selection.w,
                    cutHeight: selection.h,
                    x: selection.x,
                    y: selection.y,
                    width: selection.w,
                    height: selection.h,
                    rotated: false,
                    trimmed: false,
                    spriteSourceSize: { x: 0, y: 0, w: selection.w, h: selection.h },
                    sourceSize: { w: selection.w, h: selection.h }
                };

                // Delete old frame
                delete texture.frames[this.selectedFrame];

                // Update selectedFrame to new name
                this.selectedFrame = frameName;
}
        } else {
            // We're updating an existing frame's data
            if (texture.frames[frameName]) {
                // Update existing frame
                const frame = texture.frames[frameName];
                frame.cutX = selection.x;
                frame.cutY = selection.y;
                frame.cutWidth = selection.w;
                frame.cutHeight = selection.h;
                frame.x = selection.x;
                frame.y = selection.y;
                frame.width = selection.w;
                frame.height = selection.h;
                frame.spriteSourceSize = { x: 0, y: 0, w: selection.w, h: selection.h };
                frame.sourceSize = { w: selection.w, h: selection.h };
} else {
                // Create new frame
                texture.frames[frameName] = {
                    cutX: selection.x,
                    cutY: selection.y,
                    cutWidth: selection.w,
                    cutHeight: selection.h,
                    x: selection.x,
                    y: selection.y,
                    width: selection.w,
                    height: selection.h,
                    rotated: false,
                    trimmed: false,
                    spriteSourceSize: { x: 0, y: 0, w: selection.w, h: selection.h },
                    sourceSize: { w: selection.w, h: selection.h }
                };
}
        }
        
        // Refresh the frame list to show updated names
        this.populateFrameList(texture, atlasKey);
        
        // Update selection to the new/updated frame
        this.selectedFrame = frameName;
        this.currentSelection = selection;
        
        // Store for export
        if (!this.frameUpdates) this.frameUpdates = {};
        if (!this.frameUpdates[atlasKey]) this.frameUpdates[atlasKey] = {};
        this.frameUpdates[atlasKey][frameName] = {
            filename: frameName,
            frame: {
                x: selection.x,
                y: selection.y,
                w: selection.w,
                h: selection.h
            },
            rotated: false,
            trimmed: false,
            spriteSourceSize: {
                x: 0,
                y: 0,
                w: selection.w,
                h: selection.h
            },
            sourceSize: {
                w: selection.w,
                h: selection.h
            }
        };
    }
    
    exportSpriteJSON(atlasKey) {
        const scene = game.scene.getScene('MinigameScene');
        const texture = scene.textures.get(atlasKey);
        const sourceImage = texture.source[0].image;
        
        // Build JSON structure
        const jsonData = {
            frames: [],
            meta: {
                version: "1.0",
                image: atlasKey.includes('bug') ? "bug-minigame-spritesheet.png" : "watering-minigame-spritesheet.png",
                format: "RGBA8888",
                size: {
                    w: sourceImage.width,
                    h: sourceImage.height
                },
                scale: "1"
            }
        };
        
        // Add existing frames
        Object.keys(texture.frames).forEach(frameName => {
            if (frameName === '__BASE') return;
            
            const frame = texture.frames[frameName];
            jsonData.frames.push({
                filename: frameName,
                frame: {
                    x: frame.x,
                    y: frame.y,
                    w: frame.width,
                    h: frame.height
                },
                rotated: false,
                trimmed: false,
                spriteSourceSize: {
                    x: 0,
                    y: 0,
                    w: frame.width,
                    h: frame.height
                },
                sourceSize: {
                    w: frame.width,
                    h: frame.height
                }
            });
        });
        
        // Add any updates
        if (this.frameUpdates && this.frameUpdates[atlasKey]) {
            Object.values(this.frameUpdates[atlasKey]).forEach(frameData => {
                jsonData.frames.push(frameData);
            });
        }
        
        // Download JSON
        const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${atlasKey}-updated.json`;
        a.click();
        URL.revokeObjectURL(url);
}
    
    disableGameInput() {
        // Disable game keyboard input while sprite editor is open
        const scene = game.scene.getScene('MinigameScene');
        if (scene) {
            // Store original enabled state
            this.originalInputState = {
                keyAEnabled: scene.keyA ? scene.keyA.enabled : false,
                keyDEnabled: scene.keyD ? scene.keyD.enabled : false,
                cursorsEnabled: scene.cursors ? Object.keys(scene.cursors).map(key => ({
                    key: key,
                    enabled: scene.cursors[key].enabled
                })) : []
            };
            
            // Disable all game inputs
            if (scene.keyA) scene.keyA.enabled = false;
            if (scene.keyD) scene.keyD.enabled = false;
            if (scene.cursors) {
                Object.keys(scene.cursors).forEach(key => {
                    if (scene.cursors[key]) scene.cursors[key].enabled = false;
                });
            }
}
    }
    
    enableGameInput() {
        // Re-enable game keyboard input when sprite editor is closed
        const scene = game.scene.getScene('MinigameScene');
        if (scene && this.originalInputState) {
            // Restore original enabled state
            if (scene.keyA) scene.keyA.enabled = this.originalInputState.keyAEnabled;
            if (scene.keyD) scene.keyD.enabled = this.originalInputState.keyDEnabled;
            if (scene.cursors) {
                this.originalInputState.cursorsEnabled.forEach(keyState => {
                    if (scene.cursors[keyState.key]) {
                        scene.cursors[keyState.key].enabled = keyState.enabled;
                    }
                });
            }
this.originalInputState = null;
        }
    }
}

// Set up UI controls
document.addEventListener('DOMContentLoaded', () => {
    // Initialize developer console after DOM is ready
    const devConsole = new DevConsole();
    window.devConsole = devConsole;
    
    // Initialize sprite tools after DOM is ready
    const spriteTools = new SpriteTools();
    window.spriteTools = spriteTools;

    const resetGameBtn = document.getElementById('resetGame');
    if (resetGameBtn) {
        resetGameBtn.addEventListener('click', () => {
            const scene = game.scene.getScene('MinigameScene');
            if (scene) {
                devConsole.logEvent('Resetting game');
                // Clear all sprites and reset the scene
                scene.children.removeAll();
            }
            
            // Close sprite editor if open
            const spriteEditor = document.getElementById('fullScreenSpriteEditor');
            if (spriteEditor) {
                spriteEditor.remove();
            }
            
            // Reset sprite tools upload system
            if (window.spriteTools) {
                window.spriteTools.resetToStep1();
            }
            
            // Show upload panel
            const uploadPanel = document.getElementById('uploadPanel');
            if (uploadPanel) {
                uploadPanel.style.display = 'block';
            }
            
            // Hide sprite tools panel
            const spriteToolsPanel = document.getElementById('spriteToolsPanel');
            if (spriteToolsPanel) {
                spriteToolsPanel.classList.add('hidden');
            }
});
    }
    
    // Help button handler
    const helpBtn = document.getElementById('helpBtn');
    if (helpBtn) {
        helpBtn.addEventListener('click', () => {
            helpUI.show();
});
    }

    // Update engine status
    devConsole.updateEngineStatus('Initializing...');
    
    // Update game container and ensure canvas is attached
    const gameContainer = document.getElementById('gameContainer');
    if (gameContainer) {
        gameContainer.innerHTML = ''; // Clear any placeholder content
        
        // Force attach the canvas to the container
        setTimeout(() => {
            const canvas = game.canvas;
            if (canvas && !canvas.parentElement) {
gameContainer.appendChild(canvas);
                devConsole.updateEngineStatus('Canvas Attached');
            }
        }, 100);
    }
    
    setTimeout(() => {
        devConsole.updateEngineStatus('Ready');
        
        // Force input system refresh after everything is mounted
        if (game && game.scale) {
            game.scale.refresh();
}
    }, 2000);
});
